/**
 * Database Seeding
 *
 * Populates the mock database with initial data for development.
 */

import type {
  User,
  ChallengeCategory,
  Challenge,
  Community,
  CommunityMembership,
  FeedItem,
  FeedComment,
  Badge,
  UserBadge,
  XPEvent,
  UserChallenge,
  Gift,
  CommunityAssignment,
  CommunityStudentProgress,
  RejectionReasonTemplate,
  School,
  SchoolClass,
  StudentRoster,
  TeacherRoster,
  GradeLevel,
  ClassAssignment,
  PurchaseRequest,
  CommunityGoal,
  Vendor,
  ProviderFeedback,
  TrackRecord,
  TrackRecordEntry,
  TrackRecordEntryMedia,
  TrackRecordComment,
} from '../types'
import { mockDb, generateId, timestamp } from './mockDb'
import { mockHash } from './client'

/**
 * Seed challenge categories
 */
function seedCategories(): void {
  const categories: Array<Omit<ChallengeCategory, 'createdAt' | 'updatedAt'>> = [
    { id: generateId(), name: 'Outdoor & Nature', slug: 'outdoor-nature', displayOrder: 1, icon: 'tree', isActive: true },
    { id: generateId(), name: 'Building & Making', slug: 'building-making', displayOrder: 2, icon: 'hammer', isActive: true },
    { id: generateId(), name: 'Music & Sound', slug: 'music-sound', displayOrder: 3, icon: 'music', isActive: true },
    { id: generateId(), name: 'Cooking & Food', slug: 'cooking-food', displayOrder: 4, icon: 'utensils', isActive: true },
    { id: generateId(), name: 'Art & Creativity', slug: 'art-creativity', displayOrder: 5, icon: 'palette', isActive: true },
    { id: generateId(), name: 'Science & Exploration', slug: 'science-exploration', displayOrder: 6, icon: 'flask', isActive: true },
    { id: generateId(), name: 'Fitness & Movement', slug: 'fitness-movement', displayOrder: 7, icon: 'running', isActive: true },
    { id: generateId(), name: 'Community & Service', slug: 'community-service', displayOrder: 8, icon: 'heart', isActive: true },
    { id: generateId(), name: 'Life Skills', slug: 'life-skills', displayOrder: 9, icon: 'star', isActive: true },
    { id: generateId(), name: 'Technology & Digital Creation', slug: 'technology-digital', displayOrder: 10, icon: 'laptop', isActive: true },
  ]

  for (const category of categories) {
    mockDb.insertWithId('categories', {
      ...category,
      createdAt: timestamp(),
      updatedAt: timestamp(),
    } as ChallengeCategory)
  }
}

/**
 * Seed demo users
 */
function seedUsers(): void {
  const now = timestamp()

  // Demo user (regular user)
  const demoUser: User & { passwordHash: string } = {
    id: 'demo-user-001',
    email: 'demo@docurious.com',
    passwordHash: mockHash('DcDemo_2026!'),
    displayName: 'Demo User',
    dateOfBirth: '1990-01-15',
    role: 'user',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: ['outdoor-nature', 'cooking-food', 'art-creativity'],
    strengths: ['cooking-food'],
    aspirations: ['science-exploration'],
    onboardingCompleted: true,
    createdAt: now,
    updatedAt: now,
  }

  // School admin
  const schoolAdmin: User & { passwordHash: string } = {
    id: 'school-admin-001',
    email: 'admin@lincoln-elementary.edu',
    passwordHash: mockHash('DcAdmin_2026!'),
    displayName: 'Sarah Johnson',
    dateOfBirth: '1985-06-20',
    role: 'school_admin',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: [],
    schoolId: 'school-001',
    createdAt: now,
    updatedAt: now,
  }

  // Teacher
  const teacher: User & { passwordHash: string } = {
    id: 'teacher-001',
    email: 'teacher@lincoln-elementary.edu',
    passwordHash: mockHash('DcTeach_2026!'),
    displayName: 'Mr. Thompson',
    dateOfBirth: '1982-03-10',
    role: 'teacher',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: ['science-exploration', 'outdoor-nature'],
    schoolId: 'school-001',
    createdAt: now,
    updatedAt: now,
  }

  // Student (under 13, tier 2)
  const student: User & { passwordHash: string } = {
    id: 'student-001',
    email: 'student@lincoln-elementary.edu',
    passwordHash: mockHash('DcStudent_2026!'),
    displayName: 'Alex Student',
    dateOfBirth: '2015-09-05',
    role: 'student',
    accountTier: 'tier_2_full',
    accountStatus: 'active',
    isUnder13: true,
    parentalConsentStatus: 'granted',
    interests: ['building-making', 'technology-digital'],
    strengths: ['building-making'],
    aspirations: ['technology-digital'],
    onboardingCompleted: true,
    schoolId: 'school-001',
    createdAt: now,
    updatedAt: now,
  }

  // Vendor
  const vendor: User & { passwordHash: string } = {
    id: 'vendor-001',
    email: 'vendor@craftbox.com',
    passwordHash: mockHash('DcVendor_2026!'),
    displayName: 'CraftBox',
    dateOfBirth: '1975-01-01', // Company founding date as placeholder
    role: 'vendor',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: [],
    vendorProfileId: 'vendor-profile-001',
    createdAt: now,
    updatedAt: now,
  }

  // Platform admin - full system access
  const platformAdmin: User & { passwordHash: string } = {
    id: 'platform-admin-001',
    email: 'platform@docurious.com',
    passwordHash: mockHash('DcPlatfm_2026!'),
    displayName: 'Platform Admin',
    dateOfBirth: '1980-01-01',
    role: 'platform_admin',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: [],
    createdAt: now,
    updatedAt: now,
  }

  // Head school admin - manages multiple schools
  const headSchoolAdmin: User & { passwordHash: string } = {
    id: 'head-school-admin-001',
    email: 'head-admin@lincoln-elementary.edu',
    passwordHash: mockHash('DcHeadAd_2026!'),
    displayName: 'District Admin',
    dateOfBirth: '1978-04-15',
    role: 'head_school_admin',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: [],
    schoolId: 'school-001',
    createdAt: now,
    updatedAt: now,
  }

  // Parent - linked to student
  const parent: User & { passwordHash: string } = {
    id: 'parent-001',
    email: 'parent@example.com',
    passwordHash: mockHash('DcParent_2026!'),
    displayName: 'Alex Parent',
    dateOfBirth: '1985-07-20',
    role: 'parent',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: ['outdoor-nature', 'cooking-food'],
    linkedChildIds: ['student-001'],
    createdAt: now,
    updatedAt: now,
  }

  // Staff - DoCurious internal staff
  const staff: User & { passwordHash: string } = {
    id: 'staff-001',
    email: 'staff@docurious.com',
    passwordHash: mockHash('DcStaff_2026!'),
    displayName: 'Content Manager',
    dateOfBirth: '1988-11-10',
    role: 'staff',
    accountTier: 'standard',
    accountStatus: 'active',
    isUnder13: false,
    parentalConsentStatus: 'not_required',
    interests: [],
    createdAt: now,
    updatedAt: now,
  }

  // Tier 1 student (school-only, no parent link)
  const studentTier1: User & { passwordHash: string } = {
    id: 'student-tier1-001',
    email: 'emma@lincoln-elementary.edu',
    passwordHash: mockHash('DcStudent_2026!'),
    displayName: 'Emma Young',
    dateOfBirth: '2015-06-15',
    role: 'student',
    accountTier: 'tier_1_school_only',
    accountStatus: 'active',
    isUnder13: true,
    parentalConsentStatus: 'pending',
    interests: [],
    schoolId: 'school-001',
    createdAt: now,
    updatedAt: now,
  }

  mockDb.insertWithId('users', demoUser as unknown as User)
  mockDb.insertWithId('users', schoolAdmin as unknown as User)
  mockDb.insertWithId('users', teacher as unknown as User)
  mockDb.insertWithId('users', student as unknown as User)
  mockDb.insertWithId('users', studentTier1 as unknown as User)
  mockDb.insertWithId('users', vendor as unknown as User)
  mockDb.insertWithId('users', platformAdmin as unknown as User)
  mockDb.insertWithId('users', headSchoolAdmin as unknown as User)
  mockDb.insertWithId('users', parent as unknown as User)
  mockDb.insertWithId('users', staff as unknown as User)

  // Additional teachers for school roster
  const additionalTeachers: Array<User & { passwordHash: string }> = [
    { id: 'teacher-002', email: 'mrs.chen@lincoln-elementary.edu', passwordHash: mockHash('DcTeach_2026!'), displayName: 'Mrs. Chen', dateOfBirth: '1985-04-15', role: 'teacher', accountTier: 'standard', accountStatus: 'active', isUnder13: false, parentalConsentStatus: 'not_required', interests: ['art-creativity', 'cooking-food'], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'teacher-003', email: 'mr.rodriguez@lincoln-elementary.edu', passwordHash: mockHash('DcTeach_2026!'), displayName: 'Mr. Rodriguez', dateOfBirth: '1979-09-22', role: 'teacher', accountTier: 'standard', accountStatus: 'active', isUnder13: false, parentalConsentStatus: 'not_required', interests: ['science-exploration', 'technology-digital'], schoolId: 'school-001', createdAt: now, updatedAt: now },
  ]
  for (const t of additionalTeachers) {
    mockDb.insertWithId('users', t as unknown as User)
  }

  // Mock students for class community assignment progress display
  const mockStudents: Array<User & { passwordHash: string }> = [
    { id: 'mock-student-002', email: 'emma@school.edu', passwordHash: '', displayName: 'Emma Johnson', dateOfBirth: '2015-03-12', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-003', email: 'michael@school.edu', passwordHash: '', displayName: 'Michael Smith', dateOfBirth: '2015-05-22', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-004', email: 'sophia@school.edu', passwordHash: '', displayName: 'Sophia Garcia', dateOfBirth: '2015-01-08', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-005', email: 'james@school.edu', passwordHash: '', displayName: 'James Wilson', dateOfBirth: '2015-07-30', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-006', email: 'olivia@school.edu', passwordHash: '', displayName: 'Olivia Brown', dateOfBirth: '2015-11-15', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    // Additional students for Class 4A and Class 6A
    { id: 'mock-student-007', email: 'liam@school.edu', passwordHash: '', displayName: 'Liam Martinez', dateOfBirth: '2016-02-10', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-008', email: 'ava@school.edu', passwordHash: '', displayName: 'Ava Thompson', dateOfBirth: '2016-06-25', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-009', email: 'noah@school.edu', passwordHash: '', displayName: 'Noah Davis', dateOfBirth: '2016-01-03', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-010', email: 'isabella@school.edu', passwordHash: '', displayName: 'Isabella Anderson', dateOfBirth: '2014-09-18', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-011', email: 'ethan@school.edu', passwordHash: '', displayName: 'Ethan Taylor', dateOfBirth: '2014-11-30', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-012', email: 'mia@school.edu', passwordHash: '', displayName: 'Mia Thomas', dateOfBirth: '2014-04-07', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
    { id: 'mock-student-013', email: 'lucas@school.edu', passwordHash: '', displayName: 'Lucas Jackson', dateOfBirth: '2014-08-14', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
  ]

  for (const mockStudent of mockStudents) {
    mockDb.insertWithId('users', mockStudent as unknown as User)
  }
}

/**
 * Seed sample challenges
 */
function seedChallenges(): void {
  const now = timestamp()
  const categories = mockDb.getAll<ChallengeCategory>('categories')

  const getCategoryId = (slug: string) =>
    categories.find((c) => c.slug === slug)?.id ?? ''

  const challenges: Array<Partial<Challenge>> = [
    {
      id: 'challenge-001',
      title: 'Build a Birdhouse',
      description: 'Create a cozy home for feathered friends! This challenge guides you through building a simple birdhouse from basic materials. Perfect for beginners who want to learn woodworking basics.',
      shortDescription: 'Build a birdhouse from scratch',
      source: 'staff',
      categoryIds: [getCategoryId('building-making'), getCategoryId('outdoor-nature')],
      difficulty: 'beginner',
      estimatedDuration: '2-3 hours',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
      instructions: '1. Gather materials\n2. Cut pieces\n3. Assemble\n4. Paint (optional)\n5. Hang outdoors',
      materialsNeeded: 'Wood planks, nails, hammer, saw, sandpaper, outdoor paint (optional)',
      teacherTips: 'This challenge works well as a group activity. Consider pairing students of different skill levels. Allow 2 class periods for planning and 1 for a debrief discussion. Connects well to biology (habitats) and woodworking curriculum.',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 100,
      status: 'approved',
      approvedAt: now,
      completionCount: 47,
      trackRecordCount: 38,
      isFeatured: true,
      specificMission: 'Build a functional birdhouse that can be mounted outdoors and attract at least one bird species.',
      importantInformation: 'Adult supervision recommended when using saws and hammers. Wear safety goggles when cutting wood. Use outdoor-grade materials if mounting outside. Avoid pressure-treated wood as it can be harmful to birds.',
      verificationRequirements: [
        { id: 'vr-001-1', description: 'Photo of completed birdhouse from front', isRequired: true, displayOrder: 1 },
        { id: 'vr-001-2', description: 'Photo of birdhouse mounted or ready to mount', isRequired: true, displayOrder: 2 },
        { id: 'vr-001-3', description: 'Brief description of materials used', isRequired: false, displayOrder: 3 },
      ],
      purchaseLinks: [
        { label: 'Birdhouse Wood Kit (Amazon)', url: 'https://example.com/birdhouse-kit' },
      ],
      resourceLinks: [
        { label: 'Step-by-Step Video Tutorial', url: 'https://example.com/birdhouse-tutorial' },
        { label: 'Bird Species Guide by Region', url: 'https://example.com/bird-guide' },
      ],
      galleryImageUrls: [
        'https://images.unsplash.com/photo-1520857014576-2c4f4c972b57?w=800',
        'https://images.unsplash.com/photo-1591694823904-05f47c9df04a?w=800',
        'https://images.unsplash.com/photo-1552083974-186346df2da0?w=800',
      ],
      videoUrl: 'https://www.youtube.com/watch?v=example-birdhouse',
      milestones: [
        { id: 'ms-001-1', challengeId: 'challenge-001', title: 'Gather Materials', description: 'Collect all wood, nails, and tools needed', orderNumber: 1, promptText: 'What materials did you gather?' },
        { id: 'ms-001-2', challengeId: 'challenge-001', title: 'Cut Wood Pieces', description: 'Cut all pieces to the correct dimensions', orderNumber: 2, promptText: 'Show your cut pieces' },
        { id: 'ms-001-3', challengeId: 'challenge-001', title: 'Assemble the Birdhouse', description: 'Nail or screw pieces together', orderNumber: 3, promptText: 'Document the assembly process' },
        { id: 'ms-001-4', challengeId: 'challenge-001', title: 'Finish and Mount', description: 'Paint or seal, then mount outdoors', orderNumber: 4, promptText: 'Share a photo of your finished birdhouse!' },
      ],
      createdAt: now,
      updatedAt: now,
      skills: ['Woodworking', 'Spatial Planning', 'Hand Tools', 'Problem Solving'],
    },
    {
      id: 'challenge-002',
      title: 'Cook a Meal from Another Culture',
      description: 'Expand your culinary horizons by preparing an authentic dish from a cuisine you\'ve never tried before. Research the culture, source authentic ingredients, and document your cooking journey.',
      shortDescription: 'Explore global cuisines through cooking',
      source: 'staff',
      categoryIds: [getCategoryId('cooking-food')],
      difficulty: 'intermediate',
      estimatedDuration: '3-4 hours',
      ageMin: 10,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800',
      instructions: 'Choose a cuisine, research recipes, gather ingredients, cook, and share!',
      materialsNeeded: 'Ingredients based on chosen recipe, basic kitchen equipment',
      locationType: 'indoor',
      hasMilestones: false,
      baseXp: 150,
      status: 'approved',
      approvedAt: now,
      completionCount: 125,
      trackRecordCount: 98,
      isFeatured: true,
      createdAt: now,
      updatedAt: now,
      skills: ['Culinary Technique', 'Cultural Awareness', 'Time Management', 'Recipe Reading'],
    },
    {
      id: 'challenge-003',
      title: 'Complete a 5K Run',
      description: 'Train for and complete a 5K run! This structured challenge takes you from couch to 5K with a proven training plan. Track your progress and celebrate at the finish line.',
      shortDescription: 'Train for and complete your first 5K',
      source: 'staff',
      categoryIds: [getCategoryId('fitness-movement')],
      difficulty: 'intermediate',
      estimatedDuration: '6-8 weeks',
      ageMin: 12,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800',
      instructions: 'Follow the 8-week training plan, document your runs, complete the 5K!',
      materialsNeeded: 'Running shoes, comfortable clothes, water bottle',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 300,
      status: 'approved',
      approvedAt: now,
      completionCount: 89,
      trackRecordCount: 72,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Endurance Training', 'Goal Setting', 'Self-Discipline', 'Pacing'],
    },
    {
      id: 'challenge-004',
      title: 'Robotics Starter Kit',
      description: 'Build your first robot with this comprehensive kit! Learn about circuits, motors, and basic programming while assembling a robot that can navigate obstacles.',
      shortDescription: 'Build and program your first robot',
      source: 'vendor',
      vendorId: 'vendor-001',
      categoryIds: [getCategoryId('technology-digital'), getCategoryId('building-making')],
      difficulty: 'intermediate',
      estimatedDuration: '4-6 hours',
      ageMin: 10,
      ageMax: 16,
      soloGroup: 'solo',
      isFree: false,
      costAmount: 49.99,
      costCurrency: 'USD',
      costDisplay: '$49.99',
      fulfillmentType: 'kit',
      coverImageUrl: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800',
      instructions: 'Included in kit materials',
      materialsNeeded: 'All materials included in kit',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 200,
      status: 'approved',
      approvedAt: now,
      completionCount: 34,
      trackRecordCount: 28,
      isFeatured: true,
      vendorName: 'CraftBox',
      specificMission: 'Build a robot that can navigate around at least 3 obstacles without human intervention.',
      importantInformation: 'Contains small parts — not suitable for children under 8 without supervision. Requires 4 AA batteries (not included). Keep away from water. Adult supervision recommended for soldering steps.',
      verificationRequirements: [
        { id: 'vr-004-1', description: 'Photo of fully assembled robot', isRequired: true, displayOrder: 1 },
        { id: 'vr-004-2', description: 'Video of robot navigating obstacles (15-30 seconds)', isRequired: true, displayOrder: 2 },
        { id: 'vr-004-3', description: 'Description of any modifications you made', isRequired: false, displayOrder: 3 },
      ],
      purchaseLinks: [
        { label: 'CraftBox Robotics Kit', url: 'https://example.com/robotics-kit' },
        { label: 'Extra Sensor Pack', url: 'https://example.com/sensor-pack' },
      ],
      resourceLinks: [
        { label: 'Assembly Guide (PDF)', url: 'https://example.com/robotics-assembly' },
        { label: 'Programming Tutorial', url: 'https://example.com/robotics-programming' },
        { label: 'Troubleshooting FAQ', url: 'https://example.com/robotics-faq' },
      ],
      galleryImageUrls: [
        'https://images.unsplash.com/photo-1561557944-6e7860d1a7eb?w=800',
        'https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=800',
        'https://images.unsplash.com/photo-1518314916381-77a37c2a49ae?w=800',
      ],
      milestones: [
        { id: 'ms-004-1', challengeId: 'challenge-004', title: 'Unbox and Inventory', description: 'Check all parts are included', orderNumber: 1, promptText: 'Lay out all parts for a photo' },
        { id: 'ms-004-2', challengeId: 'challenge-004', title: 'Build the Chassis', description: 'Assemble the main body and wheel system', orderNumber: 2, promptText: 'Show your assembled chassis' },
        { id: 'ms-004-3', challengeId: 'challenge-004', title: 'Wire the Electronics', description: 'Connect motors, sensors, and controller', orderNumber: 3, promptText: 'Document your wiring' },
        { id: 'ms-004-4', challengeId: 'challenge-004', title: 'Program and Test', description: 'Upload code and run obstacle course', orderNumber: 4, promptText: 'Record a video of your robot in action!' },
      ],
      createdAt: now,
      updatedAt: now,
      skills: ['Circuit Design', 'Basic Programming', 'Engineering Thinking', 'Problem Solving'],
    },
    {
      id: 'challenge-005',
      title: 'Plant a Vegetable Garden',
      description: 'Grow your own food! This challenge guides you through planning, planting, and nurturing a small vegetable garden. Watch your food grow from seed to table.',
      shortDescription: 'Start a backyard vegetable garden',
      source: 'staff',
      categoryIds: [getCategoryId('outdoor-nature'), getCategoryId('life-skills')],
      difficulty: 'beginner',
      estimatedDuration: '8-12 weeks',
      ageMin: 6,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800',
      instructions: 'Plan your garden, prepare soil, plant seeds, water and tend, harvest!',
      materialsNeeded: 'Seeds, soil, basic gardening tools, containers or garden bed',
      teacherTips: 'Great for science classes — ties into plant biology, ecosystems, and sustainability. Start in early spring for best results. Students can maintain a shared class garden or individual pots. Use the reflection prompts to connect to nutrition and environmental science.',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 250,
      status: 'approved',
      approvedAt: now,
      completionCount: 67,
      trackRecordCount: 54,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Botany', 'Patience', 'Environmental Science', 'Planning'],
    },
    {
      id: 'challenge-006',
      title: 'Learn to Play Ukulele',
      description: 'Pick up this beginner-friendly instrument and learn 5 songs in 30 days. The ukulele is perfect for all ages and surprisingly easy to start with.',
      shortDescription: 'Master 5 songs on ukulele in 30 days',
      source: 'staff',
      categoryIds: [getCategoryId('music-sound')],
      difficulty: 'beginner',
      estimatedDuration: '30 days',
      ageMin: 6,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: false,
      costAmount: 29.99,
      costCurrency: 'USD',
      costDisplay: '$29.99',
      coverImageUrl: 'https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=800',
      instructions: 'Get a ukulele, learn basic chords, practice daily, master 5 songs',
      materialsNeeded: 'Ukulele, tuner app, chord chart',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 200,
      status: 'approved',
      approvedAt: now,
      completionCount: 156,
      trackRecordCount: 134,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Musical Ear', 'Rhythm', 'Chord Memory', 'Practice Discipline'],
    },
    {
      id: 'challenge-007',
      title: 'Watercolor Painting Basics',
      description: 'Discover the beauty of watercolor painting through guided exercises. Learn wet-on-wet, dry brush, and glazing techniques while creating 5 finished paintings.',
      shortDescription: 'Create 5 watercolor paintings',
      source: 'staff',
      categoryIds: [getCategoryId('art-creativity')],
      difficulty: 'beginner',
      estimatedDuration: '2 weeks',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=800',
      instructions: 'Learn techniques, practice exercises, create 5 paintings',
      materialsNeeded: 'Watercolor paints, brushes, watercolor paper, water containers',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 150,
      status: 'approved',
      approvedAt: now,
      completionCount: 234,
      trackRecordCount: 198,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Color Theory', 'Brush Control', 'Composition', 'Creative Expression'],
    },
    {
      id: 'challenge-008',
      title: 'Volunteer at a Local Shelter',
      description: 'Make a difference in your community by volunteering at a local animal shelter or food bank. Complete 10 hours of service and document your experience.',
      shortDescription: 'Complete 10 hours of community service',
      source: 'staff',
      categoryIds: [getCategoryId('community-service')],
      difficulty: 'beginner',
      estimatedDuration: '2-4 weeks',
      ageMin: 12,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800',
      instructions: 'Find a local organization, sign up, volunteer, document your hours',
      materialsNeeded: 'None required',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 300,
      status: 'approved',
      approvedAt: now,
      completionCount: 78,
      trackRecordCount: 65,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Empathy', 'Teamwork', 'Communication', 'Civic Responsibility'],
    },
    {
      id: 'challenge-009',
      title: 'Build a Terrarium',
      description: 'Create a self-sustaining mini ecosystem in a glass container. Learn about plant care, humidity, and the water cycle while building something beautiful.',
      shortDescription: 'Create a self-sustaining glass garden',
      source: 'staff',
      categoryIds: [getCategoryId('science-exploration'), getCategoryId('outdoor-nature')],
      difficulty: 'beginner',
      estimatedDuration: '2-3 hours',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1485955900006-10f4d324d411?w=800',
      instructions: 'Layer materials, plant, seal, and observe',
      materialsNeeded: 'Glass container, pebbles, charcoal, soil, small plants, moss',
      locationType: 'indoor',
      hasMilestones: false,
      baseXp: 100,
      status: 'approved',
      approvedAt: now,
      completionCount: 189,
      trackRecordCount: 167,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Ecosystem Design', 'Plant Care', 'Scientific Observation', 'Patience'],
    },
    {
      id: 'challenge-010',
      title: 'Learn Basic Origami',
      description: 'Master the ancient art of paper folding. Create 10 different origami designs from cranes to flowers using just paper and patience.',
      shortDescription: 'Fold 10 different origami designs',
      source: 'staff',
      categoryIds: [getCategoryId('art-creativity')],
      difficulty: 'beginner',
      estimatedDuration: '1 week',
      ageMin: 6,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1604580864964-0462f5d5b1a8?w=800',
      instructions: 'Start with simple designs, work up to complex ones',
      materialsNeeded: 'Origami paper or square paper',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 75,
      status: 'approved',
      approvedAt: now,
      completionCount: 312,
      trackRecordCount: 287,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Precision', 'Spatial Reasoning', 'Following Instructions', 'Fine Motor Skills'],
    },
    {
      id: 'challenge-011',
      title: 'Hike a New Trail',
      description: 'Explore nature by completing a hike on a trail you have never been on before. Document the wildlife, plants, and views you discover along the way.',
      shortDescription: 'Explore a new hiking trail',
      source: 'staff',
      categoryIds: [getCategoryId('outdoor-nature'), getCategoryId('fitness-movement')],
      difficulty: 'beginner',
      estimatedDuration: '3-5 hours',
      ageMin: 6,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800',
      instructions: 'Find a trail, prepare supplies, hike, document discoveries',
      materialsNeeded: 'Comfortable shoes, water, snacks, camera',
      locationType: 'outdoor',
      hasMilestones: false,
      baseXp: 100,
      status: 'approved',
      approvedAt: now,
      completionCount: 423,
      trackRecordCount: 389,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Navigation', 'Endurance', 'Nature Identification', 'Self-Reliance'],
    },
    {
      id: 'challenge-012',
      title: 'Bake Bread from Scratch',
      description: 'Experience the satisfaction of baking your own bread. Learn about yeast, kneading, and the science of baking while creating delicious homemade loaves.',
      shortDescription: 'Bake homemade bread',
      source: 'staff',
      categoryIds: [getCategoryId('cooking-food'), getCategoryId('life-skills')],
      difficulty: 'intermediate',
      estimatedDuration: '4-5 hours',
      ageMin: 10,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=800',
      instructions: 'Mix ingredients, knead, let rise, shape, bake',
      materialsNeeded: 'Flour, yeast, salt, water, mixing bowl, oven',
      locationType: 'indoor',
      hasMilestones: false,
      baseXp: 125,
      status: 'approved',
      approvedAt: now,
      completionCount: 267,
      trackRecordCount: 234,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Baking Science', 'Time Management', 'Following Recipes', 'Patience'],
    },
    {
      id: 'challenge-013',
      title: 'Learn to Juggle',
      description: 'Master the art of juggling with 3 balls. This fun skill builds hand-eye coordination and patience. Start with scarves and work up to balls.',
      shortDescription: 'Juggle 3 balls for 30 seconds',
      source: 'staff',
      categoryIds: [getCategoryId('fitness-movement')],
      difficulty: 'intermediate',
      estimatedDuration: '2 weeks',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
      instructions: 'Start with one ball, add more as you improve',
      materialsNeeded: 'Juggling balls or tennis balls',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 150,
      status: 'approved',
      approvedAt: now,
      completionCount: 145,
      trackRecordCount: 123,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Hand-Eye Coordination', 'Rhythm', 'Persistence', 'Motor Skills'],
    },
    {
      id: 'challenge-014',
      title: 'Build a Model Rocket',
      description: 'Construct and launch your own model rocket! Learn about aerodynamics, propulsion, and safety while building a rocket that can soar hundreds of feet.',
      shortDescription: 'Build and launch a model rocket',
      source: 'vendor',
      vendorId: 'vendor-001',
      categoryIds: [getCategoryId('science-exploration'), getCategoryId('building-making')],
      difficulty: 'intermediate',
      estimatedDuration: '4-6 hours',
      ageMin: 10,
      ageMax: 99,
      soloGroup: 'both',
      isFree: false,
      costAmount: 34.99,
      costCurrency: 'USD',
      costDisplay: '$34.99',
      coverImageUrl: 'https://images.unsplash.com/photo-1517976487492-5750f3195933?w=800',
      instructions: 'Follow kit instructions to build and launch safely',
      materialsNeeded: 'Model rocket kit (included)',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 200,
      status: 'approved',
      approvedAt: now,
      completionCount: 89,
      trackRecordCount: 76,
      isFeatured: true,
      vendorName: 'CraftBox',
      specificMission: 'Build and successfully launch a model rocket to at least 200 feet altitude with safe recovery.',
      importantInformation: 'SAFETY FIRST: Only launch in open fields away from buildings, trees, and power lines. Never aim at people or animals. Adult supervision required for engine installation and launch. Check local regulations for model rocket launches in your area. Do not launch in windy conditions (>15 mph).',
      verificationRequirements: [
        { id: 'vr-014-1', description: 'Photo of assembled rocket before launch', isRequired: true, displayOrder: 1 },
        { id: 'vr-014-2', description: 'Photo or video of launch', isRequired: true, displayOrder: 2 },
        { id: 'vr-014-3', description: 'Photo of recovered rocket after landing', isRequired: false, displayOrder: 3 },
      ],
      purchaseLinks: [
        { label: 'CraftBox Model Rocket Kit', url: 'https://example.com/model-rocket-kit' },
        { label: 'Extra Engines (3-pack)', url: 'https://example.com/rocket-engines' },
      ],
      resourceLinks: [
        { label: 'NAR Safety Code', url: 'https://example.com/nar-safety' },
        { label: 'Launch Site Finder', url: 'https://example.com/launch-sites' },
      ],
      galleryImageUrls: [
        'https://images.unsplash.com/photo-1457364887197-9150188c107b?w=800',
        'https://images.unsplash.com/photo-1541185933-ef5d8ed016c2?w=800',
      ],
      milestones: [
        { id: 'ms-014-1', challengeId: 'challenge-014', title: 'Build the Airframe', description: 'Construct the rocket body and nose cone', orderNumber: 1, promptText: 'Show your assembled airframe' },
        { id: 'ms-014-2', challengeId: 'challenge-014', title: 'Attach Fins and Paint', description: 'Glue fins and apply finish', orderNumber: 2, promptText: 'Show your finished rocket' },
        { id: 'ms-014-3', challengeId: 'challenge-014', title: 'Prepare Recovery System', description: 'Pack parachute and install engine', orderNumber: 3, promptText: 'Document your pre-launch prep' },
        { id: 'ms-014-4', challengeId: 'challenge-014', title: 'Launch!', description: 'Conduct safe launch and recovery', orderNumber: 4, promptText: 'Capture the launch moment!' },
      ],
      createdAt: now,
      updatedAt: now,
      skills: ['Aerodynamics', 'Assembly Skills', 'Safety Awareness', 'Physics'],
    },
    {
      id: 'challenge-015',
      title: 'Start a Nature Journal',
      description: 'Document the natural world around you through sketches and observations. Create at least 20 entries over one month focusing on local flora and fauna.',
      shortDescription: 'Create 20 nature journal entries',
      source: 'staff',
      categoryIds: [getCategoryId('outdoor-nature'), getCategoryId('art-creativity')],
      difficulty: 'beginner',
      estimatedDuration: '1 month',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800',
      instructions: 'Get a journal, go outside daily, sketch and write observations',
      materialsNeeded: 'Notebook, pencils, colored pencils optional',
      teacherTips: 'Perfect for cross-curricular use: science (observation skills, species identification), art (sketching techniques), and ELA (descriptive writing). Assign specific observation prompts each week. Works well alongside ecology or environmental science units.',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 175,
      status: 'approved',
      approvedAt: now,
      completionCount: 98,
      trackRecordCount: 87,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Observation', 'Sketching', 'Scientific Writing', 'Mindfulness'],
    },
    {
      id: 'challenge-016',
      title: 'Sew a Simple Garment',
      description: 'Learn basic sewing by creating a simple garment like a tote bag, pillowcase, or simple skirt. Master threading, stitching, and finishing techniques.',
      shortDescription: 'Sew your first handmade item',
      source: 'staff',
      categoryIds: [getCategoryId('life-skills'), getCategoryId('art-creativity')],
      difficulty: 'intermediate',
      estimatedDuration: '3-4 hours',
      ageMin: 10,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=800',
      instructions: 'Choose a pattern, cut fabric, sew pieces together, finish edges',
      materialsNeeded: 'Fabric, needle, thread, scissors, pins, pattern',
      locationType: 'indoor',
      hasMilestones: false,
      baseXp: 150,
      status: 'approved',
      approvedAt: now,
      completionCount: 134,
      trackRecordCount: 112,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Sewing Technique', 'Pattern Reading', 'Measurement', 'Attention to Detail'],
    },
    {
      id: 'challenge-017',
      title: 'Create a Stop Motion Animation',
      description: 'Tell a story through stop motion animation. Create a short film using everyday objects, toys, or clay. Learn about storyboarding, framing, and editing.',
      shortDescription: 'Make a stop motion short film',
      source: 'staff',
      categoryIds: [getCategoryId('technology-digital'), getCategoryId('art-creativity')],
      difficulty: 'intermediate',
      estimatedDuration: '1 week',
      ageMin: 10,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800',
      instructions: 'Plan story, set up scene, capture frames, edit together',
      materialsNeeded: 'Smartphone or camera, tripod, objects for animation, editing app',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 225,
      status: 'approved',
      approvedAt: now,
      completionCount: 67,
      trackRecordCount: 54,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Storytelling', 'Photography', 'Video Editing', 'Patience'],
    },
    {
      id: 'challenge-018',
      title: 'Learn Chess Strategy',
      description: 'Go beyond the basics and learn intermediate chess strategies. Study openings, tactics, and endgames while playing at least 20 games.',
      shortDescription: 'Master intermediate chess tactics',
      source: 'staff',
      categoryIds: [getCategoryId('life-skills')],
      difficulty: 'intermediate',
      estimatedDuration: '1 month',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1529699211952-734e80c4d42b?w=800',
      instructions: 'Study strategies, practice puzzles, play games, analyze mistakes',
      materialsNeeded: 'Chess set or chess app',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 200,
      status: 'approved',
      approvedAt: now,
      completionCount: 178,
      trackRecordCount: 156,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Strategic Thinking', 'Pattern Recognition', 'Planning Ahead', 'Focus'],
    },
    {
      id: 'challenge-019',
      title: 'Build a Solar Oven',
      description: 'Harness the power of the sun to cook food! Build a solar oven from simple materials and use it to make a meal. Learn about renewable energy and heat transfer.',
      shortDescription: 'Cook with solar power',
      source: 'staff',
      categoryIds: [getCategoryId('science-exploration'), getCategoryId('building-making')],
      difficulty: 'intermediate',
      estimatedDuration: '4-5 hours',
      ageMin: 10,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800',
      instructions: 'Build oven from cardboard and foil, test, cook a meal',
      materialsNeeded: 'Cardboard box, aluminum foil, plastic wrap, black paper',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 175,
      status: 'approved',
      approvedAt: now,
      completionCount: 56,
      trackRecordCount: 48,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Renewable Energy', 'Heat Transfer', 'Engineering', 'Scientific Method'],
    },
    {
      id: 'challenge-020',
      title: 'Complete a Puzzle Marathon',
      description: 'Test your patience and problem-solving skills by completing a 1000+ piece jigsaw puzzle. Document your progress and strategies.',
      shortDescription: 'Finish a 1000-piece puzzle',
      source: 'staff',
      categoryIds: [getCategoryId('life-skills')],
      difficulty: 'beginner',
      estimatedDuration: '1-2 weeks',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'both',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1494059980473-813e73ee784b?w=800',
      instructions: 'Sort pieces, build edges, work sections, complete puzzle',
      materialsNeeded: '1000+ piece jigsaw puzzle, flat surface',
      locationType: 'indoor',
      hasMilestones: false,
      baseXp: 100,
      status: 'approved',
      approvedAt: now,
      completionCount: 234,
      trackRecordCount: 212,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Pattern Recognition', 'Persistence', 'Visual Processing', 'Problem Solving'],
    },
    {
      id: 'challenge-021',
      title: 'Learn Basic Photography',
      description: 'Master the fundamentals of photography including composition, lighting, and exposure. Take 50 intentional photos applying what you learn.',
      shortDescription: 'Take 50 photos using proper technique',
      source: 'staff',
      categoryIds: [getCategoryId('art-creativity'), getCategoryId('technology-digital')],
      difficulty: 'beginner',
      estimatedDuration: '2 weeks',
      ageMin: 10,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=800',
      instructions: 'Learn rules, practice daily, review and improve',
      materialsNeeded: 'Camera or smartphone',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 150,
      status: 'approved',
      approvedAt: now,
      completionCount: 289,
      trackRecordCount: 256,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Composition', 'Lighting', 'Visual Storytelling', 'Technical Skills'],
    },
    {
      id: 'challenge-022',
      title: 'Build a Soap Box Car',
      description: 'Design and build a gravity-powered soap box car. Learn about engineering, aerodynamics, and steering while creating a vehicle you can actually ride.',
      shortDescription: 'Build a gravity-powered racer',
      source: 'staff',
      categoryIds: [getCategoryId('building-making'), getCategoryId('science-exploration')],
      difficulty: 'advanced',
      estimatedDuration: '2-3 weeks',
      ageMin: 12,
      ageMax: 99,
      soloGroup: 'both',
      isFree: false,
      costAmount: 75.00,
      costCurrency: 'USD',
      costDisplay: '$75+',
      coverImageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
      instructions: 'Design, source materials, build frame, add wheels, test and refine',
      materialsNeeded: 'Wood, wheels, axles, rope for steering, tools',
      locationType: 'outdoor',
      hasMilestones: true,
      baseXp: 400,
      status: 'approved',
      approvedAt: now,
      completionCount: 23,
      trackRecordCount: 19,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Engineering Design', 'Aerodynamics', 'Woodworking', 'Testing & Iteration'],
    },
    {
      id: 'challenge-023',
      title: 'Meditate for 30 Days',
      description: 'Build a meditation practice by meditating for at least 10 minutes every day for 30 days. Track your progress and notice the changes in focus and calm.',
      shortDescription: '30-day meditation streak',
      source: 'staff',
      categoryIds: [getCategoryId('fitness-movement'), getCategoryId('life-skills')],
      difficulty: 'beginner',
      estimatedDuration: '30 days',
      ageMin: 8,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
      instructions: 'Find quiet space, set timer, focus on breath, repeat daily',
      materialsNeeded: 'Timer, quiet space, cushion optional',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 200,
      status: 'approved',
      approvedAt: now,
      completionCount: 167,
      trackRecordCount: 145,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Mindfulness', 'Self-Awareness', 'Focus', 'Stress Management'],
    },
    {
      id: 'challenge-024',
      title: 'Candle Making Workshop',
      description: 'Learn the art of candle making! Create 5 different candles using various techniques including container, pillar, and layered designs.',
      shortDescription: 'Make 5 handcrafted candles',
      source: 'vendor',
      vendorId: 'vendor-001',
      categoryIds: [getCategoryId('art-creativity'), getCategoryId('building-making')],
      difficulty: 'beginner',
      estimatedDuration: '3-4 hours',
      ageMin: 12,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: false,
      costAmount: 39.99,
      costCurrency: 'USD',
      costDisplay: '$39.99',
      coverImageUrl: 'https://images.unsplash.com/photo-1602607667080-d23d2e1f8e2f?w=800',
      instructions: 'Melt wax, add fragrance, pour, let set, trim wick',
      materialsNeeded: 'Candle making kit (included)',
      locationType: 'indoor',
      hasMilestones: false,
      baseXp: 125,
      status: 'approved',
      approvedAt: now,
      completionCount: 98,
      trackRecordCount: 89,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Craftsmanship', 'Temperature Control', 'Creative Design', 'Safety Awareness'],
    },
    {
      id: 'challenge-025',
      title: 'Learn Sign Language Basics',
      description: 'Learn the basics of American Sign Language including the alphabet, numbers, and 50 common words and phrases. Connect with the deaf community.',
      shortDescription: 'Learn ASL alphabet and 50 signs',
      source: 'staff',
      categoryIds: [getCategoryId('life-skills'), getCategoryId('community-service')],
      difficulty: 'beginner',
      estimatedDuration: '2 weeks',
      ageMin: 6,
      ageMax: 99,
      soloGroup: 'solo',
      isFree: true,
      coverImageUrl: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800',
      instructions: 'Learn alphabet, practice daily, add new signs each day',
      materialsNeeded: 'ASL learning resource (app, video, or book)',
      locationType: 'indoor',
      hasMilestones: true,
      baseXp: 175,
      status: 'approved',
      approvedAt: now,
      completionCount: 145,
      trackRecordCount: 123,
      isFeatured: false,
      createdAt: now,
      updatedAt: now,
      skills: ['Visual Communication', 'Memory', 'Cultural Awareness', 'Dexterity'],
    },
  ]

  for (const challenge of challenges) {
    mockDb.insertWithId('challenges', challenge as Challenge)
  }
}

/**
 * Seed user-challenge relationships for demo user
 */
function seedUserChallenges(): void {
  const now = timestamp()
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()

  const userChallenges: Array<Partial<UserChallenge>> = [
    {
      id: 'uc-001',
      userId: 'demo-user-001',
      challengeId: 'challenge-001',
      status: 'verified',
      completionNumber: 1,
      verificationAttempts: 1,
      maxAttempts: 3,
      startedAt: twoWeeksAgo,
      submittedAt: oneWeekAgo,
      verifiedAt: threeDaysAgo,
      createdAt: twoWeeksAgo,
      updatedAt: threeDaysAgo,
    },
    {
      id: 'uc-002',
      userId: 'demo-user-001',
      challengeId: 'challenge-002',
      status: 'in_progress',
      completionNumber: 1,
      verificationAttempts: 0,
      maxAttempts: 3,
      startedAt: threeDaysAgo,
      createdAt: threeDaysAgo,
      updatedAt: now,
    },
    {
      id: 'uc-003',
      userId: 'demo-user-001',
      challengeId: 'challenge-005',
      status: 'submitted',
      completionNumber: 1,
      verificationAttempts: 1,
      maxAttempts: 3,
      startedAt: twoWeeksAgo,
      submittedAt: oneWeekAgo,
      createdAt: twoWeeksAgo,
      updatedAt: oneWeekAgo,
    },
  ]

  for (const uc of userChallenges) {
    mockDb.insertWithId('userChallenges', uc as UserChallenge)
  }
}

/**
 * Seed school administration data
 */
function seedSchoolData(): void {
  const now = timestamp()

  // ============================================================================
  // School
  // ============================================================================
  const school: School = {
    id: 'school-001',
    name: 'Lincoln Elementary School',
    address: '1200 Lincoln Ave',
    city: 'Springfield',
    state: 'IL',
    country: 'USA',
    timezone: 'America/Chicago',
    contactEmail: 'office@lincoln-elementary.edu',
    contactPhone: '(555) 123-4567',
    allowStudentSelfRegistration: false,
    requireParentalConsent: true,
    studentCount: 12,
    teacherCount: 4,
    classCount: 4,
    createdAt: now,
    updatedAt: now,
  }
  mockDb.insertWithId('schools', school)

  // ============================================================================
  // Grade Levels
  // ============================================================================
  const gradeLevels: GradeLevel[] = [
    { id: 'grade-k', schoolId: 'school-001', name: 'Kindergarten', displayOrder: 1, isActive: true, studentCount: 0, createdAt: now, updatedAt: now },
    { id: 'grade-1', schoolId: 'school-001', name: '1st Grade', displayOrder: 2, isActive: true, studentCount: 0, createdAt: now, updatedAt: now },
    { id: 'grade-2', schoolId: 'school-001', name: '2nd Grade', displayOrder: 3, isActive: true, studentCount: 0, createdAt: now, updatedAt: now },
    { id: 'grade-3', schoolId: 'school-001', name: '3rd Grade', displayOrder: 4, isActive: true, studentCount: 0, createdAt: now, updatedAt: now },
    { id: 'grade-4', schoolId: 'school-001', name: '4th Grade', displayOrder: 5, isActive: true, studentCount: 3, createdAt: now, updatedAt: now },
    { id: 'grade-5', schoolId: 'school-001', name: '5th Grade', displayOrder: 6, isActive: true, studentCount: 5, createdAt: now, updatedAt: now },
    { id: 'grade-6', schoolId: 'school-001', name: '6th Grade', displayOrder: 7, isActive: true, studentCount: 4, createdAt: now, updatedAt: now },
  ]
  for (const grade of gradeLevels) {
    mockDb.insertWithId('gradeLevels', grade)
  }

  // ============================================================================
  // Teachers (including demo user for testing)
  // ============================================================================
  const teachers: TeacherRoster[] = [
    { id: 'tr-001', schoolId: 'school-001', userId: 'school-admin-001', role: 'admin', department: 'Administration', status: 'active', createdAt: now, updatedAt: now },
    { id: 'tr-002', schoolId: 'school-001', userId: 'teacher-001', role: 'teacher', department: '5th Grade', status: 'active', createdAt: now, updatedAt: now },
    { id: 'tr-003', schoolId: 'school-001', userId: 'teacher-002', role: 'teacher', department: '4th Grade', status: 'active', createdAt: now, updatedAt: now },
    { id: 'tr-004', schoolId: 'school-001', userId: 'teacher-003', role: 'teacher', department: '6th Grade', status: 'active', createdAt: now, updatedAt: now },
    { id: 'tr-005', schoolId: 'school-001', userId: 'demo-user-001', role: 'admin', department: 'Administration', status: 'active', createdAt: now, updatedAt: now },
  ]
  for (const teacher of teachers) {
    mockDb.insertWithId('teacherRoster', teacher)
  }

  // ============================================================================
  // Classes
  // ============================================================================
  const classes: SchoolClass[] = [
    { id: 'class-5a', schoolId: 'school-001', name: 'Class 5A', description: 'Mr. Thompson\'s 5th Grade class', gradeLevel: '5th Grade', primaryTeacherId: 'teacher-001', isActive: true, studentCount: 5, assignmentCount: 2, createdAt: now, updatedAt: now },
    { id: 'class-5b', schoolId: 'school-001', name: 'Class 5B', description: '5th Grade Section B', gradeLevel: '5th Grade', primaryTeacherId: 'teacher-001', isActive: true, studentCount: 0, assignmentCount: 0, createdAt: now, updatedAt: now },
    { id: 'class-4a', schoolId: 'school-001', name: 'Class 4A', description: '4th Grade Homeroom', gradeLevel: '4th Grade', primaryTeacherId: 'teacher-002', isActive: true, studentCount: 3, assignmentCount: 1, createdAt: now, updatedAt: now },
    { id: 'class-6a', schoolId: 'school-001', name: 'Class 6A', description: '6th Grade Advanced', gradeLevel: '6th Grade', primaryTeacherId: 'teacher-003', isActive: true, studentCount: 4, assignmentCount: 1, createdAt: now, updatedAt: now },
  ]
  for (const cls of classes) {
    mockDb.insertWithId('classes', cls)
  }

  // ============================================================================
  // Students (roster entries linking existing mock students to school/classes)
  // ============================================================================
  const students: StudentRoster[] = [
    // Class 5A students
    { id: 'sr-001', schoolId: 'school-001', classId: 'class-5a', userId: 'mock-student-002', studentId: 'STU-2024-001', gradeLevel: '5th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-002', schoolId: 'school-001', classId: 'class-5a', userId: 'mock-student-003', studentId: 'STU-2024-002', gradeLevel: '5th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-003', schoolId: 'school-001', classId: 'class-5a', userId: 'mock-student-004', studentId: 'STU-2024-003', gradeLevel: '5th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-004', schoolId: 'school-001', classId: 'class-5a', userId: 'mock-student-005', studentId: 'STU-2024-004', gradeLevel: '5th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-005', schoolId: 'school-001', classId: 'class-5a', userId: 'mock-student-006', studentId: 'STU-2024-005', gradeLevel: '5th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    // Class 4A students
    { id: 'sr-006', schoolId: 'school-001', classId: 'class-4a', userId: 'mock-student-007', studentId: 'STU-2024-006', gradeLevel: '4th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-007', schoolId: 'school-001', classId: 'class-4a', userId: 'mock-student-008', studentId: 'STU-2024-007', gradeLevel: '4th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-008', schoolId: 'school-001', classId: 'class-4a', userId: 'mock-student-009', studentId: 'STU-2024-008', gradeLevel: '4th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    // Named students (Tier 1 and Tier 2 from role configs)
    { id: 'sr-t2-001', schoolId: 'school-001', classId: 'class-5a', userId: 'student-001', studentId: 'STU-2024-020', gradeLevel: '5th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-t1-001', schoolId: 'school-001', classId: 'class-5a', userId: 'student-tier1-001', studentId: 'STU-2024-021', gradeLevel: '5th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    // Class 6A students
    { id: 'sr-009', schoolId: 'school-001', classId: 'class-6a', userId: 'mock-student-010', studentId: 'STU-2024-009', gradeLevel: '6th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-010', schoolId: 'school-001', classId: 'class-6a', userId: 'mock-student-011', studentId: 'STU-2024-010', gradeLevel: '6th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-011', schoolId: 'school-001', classId: 'class-6a', userId: 'mock-student-012', studentId: 'STU-2024-011', gradeLevel: '6th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
    { id: 'sr-012', schoolId: 'school-001', classId: 'class-6a', userId: 'mock-student-013', studentId: 'STU-2024-012', gradeLevel: '6th Grade', status: 'active', enrolledAt: '2024-08-15T00:00:00Z', createdAt: now, updatedAt: now },
  ]
  for (const student of students) {
    mockDb.insertWithId('studentRoster', student)
  }

  // ============================================================================
  // Class Assignments
  // ============================================================================
  const oneWeekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
  const twoWeeksFromNow = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()

  const assignments: ClassAssignment[] = [
    {
      id: 'ca-001',
      classId: 'class-5a',
      challengeId: 'challenge-001', // Nature Walk
      assignedByUserId: 'teacher-001',
      title: 'Spring Nature Observation',
      instructions: 'Complete a nature walk and document at least 5 different plants or animals you observe.',
      dueDate: oneWeekFromNow,
      status: 'active',
      totalStudents: 5,
      completedCount: 2,
      submittedCount: 1,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'ca-002',
      classId: 'class-5a',
      challengeId: 'challenge-002', // Bird Watching
      assignedByUserId: 'teacher-001',
      title: 'Backyard Bird Count',
      instructions: 'Spend 30 minutes observing birds in your backyard or local park. Record the species you see.',
      dueDate: twoWeeksFromNow,
      status: 'active',
      totalStudents: 5,
      completedCount: 0,
      submittedCount: 0,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'ca-003',
      classId: 'class-4a',
      challengeId: 'challenge-006', // Basic Cooking
      assignedByUserId: 'teacher-002',
      title: 'Healthy Snack Challenge',
      instructions: 'Prepare a healthy snack with adult supervision. Take photos of your creation!',
      dueDate: oneWeekFromNow,
      status: 'active',
      totalStudents: 3,
      completedCount: 1,
      submittedCount: 0,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'ca-004',
      classId: 'class-6a',
      challengeId: 'challenge-009', // Science experiment
      assignedByUserId: 'teacher-003',
      title: 'Home Science Experiment',
      instructions: 'Choose one of the experiments from the challenge and document your results with photos and observations.',
      dueDate: twoWeeksFromNow,
      status: 'active',
      totalStudents: 4,
      completedCount: 0,
      submittedCount: 2,
      createdAt: now,
      updatedAt: now,
    },
  ]
  for (const assignment of assignments) {
    mockDb.insertWithId('classAssignments', assignment)
  }

  // ============================================================================
  // Purchase Requests (teacher requests for paid challenges)
  // ============================================================================
  const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
  const fiveDaysAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()

  const purchaseRequests: PurchaseRequest[] = [
    {
      id: 'pr-001',
      schoolId: 'school-001',
      requestedByUserId: 'teacher-001',
      challengeId: 'challenge-003',
      classId: 'class-5a',
      studentCount: 5,
      reason: 'This photography challenge would complement our nature unit perfectly. Students love taking photos!',
      estimatedCost: 24.95,
      status: 'pending',
      createdAt: twoDaysAgo,
      updatedAt: twoDaysAgo,
    },
    {
      id: 'pr-002',
      schoolId: 'school-001',
      requestedByUserId: 'teacher-002',
      challengeId: 'challenge-005',
      classId: 'class-4a',
      studentCount: 3,
      reason: 'Great hands-on activity for our STEM week.',
      estimatedCost: 14.97,
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'pr-003',
      schoolId: 'school-001',
      requestedByUserId: 'teacher-003',
      challengeId: 'challenge-007',
      classId: 'class-6a',
      studentCount: 4,
      reason: 'Would be excellent for our community service module.',
      estimatedCost: 19.96,
      status: 'approved',
      reviewedByUserId: 'school-admin-001',
      reviewedAt: fiveDaysAgo,
      reviewNote: 'Approved. Great choice for the service module.',
      createdAt: fiveDaysAgo,
      updatedAt: fiveDaysAgo,
    },
  ]
  for (const pr of purchaseRequests) {
    mockDb.insertWithId('purchaseRequests', pr)
  }
}

/**
 * Seed communities, memberships, feed items, and comments
 */
function seedCommunities(): void {
  const now = timestamp()
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
  const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()

  // --- Communities ---
  const communities: Array<Partial<Community>> = [
    // Interest/hobby communities (user-created)
    {
      id: 'community-001',
      name: 'Nature Explorers',
      description: 'A community for outdoor enthusiasts who love hiking, birdwatching, gardening, and all things nature. Share your outdoor adventures and inspire others to explore!',
      slug: 'nature-explorers',
      type: 'interest_hobby',
      ownerUserId: 'staff-001',
      visibility: 'public',
      joinModel: 'requestable',
      discussionPosting: 'all_members',
      giftingDefault: false,
      avatarUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=200',
      bannerUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200',
      memberCount: 48,
      postCount: 23,
      status: 'active',
      createdAt: '2025-06-01T00:00:00.000Z',
      updatedAt: now,
    },
    // Interest/Hobby community (public, auto-join)
    {
      id: 'community-002',
      name: 'Young Makers',
      description: 'Build, create, and invent! This community is for anyone who loves making things with their hands — from woodworking and robotics to crafts and DIY projects.',
      slug: 'young-makers',
      type: 'interest_hobby',
      ownerUserId: 'staff-001',
      visibility: 'public',
      joinModel: 'auto_join',
      discussionPosting: 'all_members',
      giftingDefault: false,
      avatarUrl: 'https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=200',
      bannerUrl: 'https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=1200',
      memberCount: 35,
      postCount: 17,
      status: 'active',
      createdAt: '2025-07-15T00:00:00.000Z',
      updatedAt: now,
    },
    // Family community (private, invite-only, gifting enabled)
    {
      id: 'community-003',
      name: 'The Thompson Family',
      description: 'Our family challenge community! Share what you\'ve been up to and plan new adventures together.',
      slug: 'thompson-family',
      type: 'family',
      ownerUserId: 'parent-001',
      visibility: 'private',
      joinModel: 'invite_only',
      discussionPosting: 'all_members',
      giftingDefault: true,
      avatarUrl: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=200',
      memberCount: 4,
      postCount: 8,
      status: 'active',
      createdAt: '2025-08-20T00:00:00.000Z',
      updatedAt: now,
    },
    // Trusted Friends community (private, invite-only, gifting enabled)
    {
      id: 'community-004',
      name: 'Adventure Buddies',
      description: 'Close friends who love trying new challenges together. Let\'s plan our next adventure!',
      slug: 'adventure-buddies',
      type: 'trusted_friends',
      ownerUserId: 'demo-user-001',
      visibility: 'private',
      joinModel: 'invite_only',
      discussionPosting: 'all_members',
      giftingDefault: true,
      memberCount: 6,
      postCount: 5,
      status: 'active',
      createdAt: '2025-09-01T00:00:00.000Z',
      updatedAt: now,
    },
    // Casual Friends community (private, invite-only, gifting off)
    {
      id: 'community-005',
      name: 'Weekend Warriors',
      description: 'A casual group for weekend activity ideas. No pressure, just fun!',
      slug: 'weekend-warriors',
      type: 'casual_friends',
      ownerUserId: 'demo-user-001',
      visibility: 'private',
      joinModel: 'invite_only',
      discussionPosting: 'all_members',
      giftingDefault: false,
      memberCount: 12,
      postCount: 3,
      status: 'active',
      createdAt: '2025-09-15T00:00:00.000Z',
      updatedAt: now,
    },
    // Institutional communities (school, roster-managed)
    {
      id: 'school-community-001',
      name: 'Lincoln Elementary - Full School',
      description: 'The whole school community! Share achievements, announcements, and celebrate together.',
      slug: 'lincoln-elementary-full',
      type: 'institutional',
      schoolId: 'school-001',
      schoolCommunityLevel: 'full_school',
      ownerUserId: 'school-admin-001',
      visibility: 'private',
      joinModel: 'roster_managed',
      discussionPosting: 'all_members',
      giftingDefault: true,
      trackRecordCommentsEnabled: false,
      isAutoMembership: true,
      avatarUrl: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=200',
      bannerUrl: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=1200',
      memberCount: 156,
      postCount: 34,
      status: 'active',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'school-community-002',
      name: '5th Grade Community',
      description: 'A community for all 5th grade students and teachers at Lincoln Elementary.',
      slug: 'lincoln-5th-grade',
      type: 'institutional',
      schoolId: 'school-001',
      schoolCommunityLevel: 'grade',
      gradeLevel: '5th Grade',
      ownerUserId: 'teacher-001',
      visibility: 'private',
      joinModel: 'roster_managed',
      discussionPosting: 'all_members',
      giftingDefault: true,
      trackRecordCommentsEnabled: false,
      isAutoMembership: true,
      avatarUrl: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=200',
      memberCount: 28,
      postCount: 15,
      status: 'active',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'school-community-003',
      name: 'Class 5A',
      description: 'Mr. Thompson\'s 5th grade class community. Share your progress and cheer on your classmates!',
      slug: 'lincoln-class-5a',
      type: 'institutional',
      schoolId: 'school-001',
      schoolCommunityLevel: 'class',
      gradeLevel: '5th Grade',
      classId: 'class-5a',
      className: 'Class 5A',
      ownerUserId: 'teacher-001',
      visibility: 'private',
      joinModel: 'roster_managed',
      discussionPosting: 'all_members',
      giftingDefault: true,
      trackRecordCommentsEnabled: true,
      isAutoMembership: true,
      avatarUrl: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=200',
      memberCount: 24,
      postCount: 8,
      status: 'active',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
  ]

  for (const community of communities) {
    mockDb.insertWithId('communities', community as Community)
  }

  // --- Memberships ---
  const memberships: Array<Partial<CommunityMembership>> = [
    // Nature Explorers members (interest_hobby)
    {
      id: 'cm-001',
      communityId: 'community-001',
      userId: 'demo-user-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: true,
      joinedAt: '2025-06-15T00:00:00.000Z',
      createdAt: '2025-06-15T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-002',
      communityId: 'community-001',
      userId: 'staff-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-06-01T00:00:00.000Z',
      createdAt: '2025-06-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-003',
      communityId: 'community-001',
      userId: 'parent-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: true,
      joinedAt: '2025-07-01T00:00:00.000Z',
      createdAt: '2025-07-01T00:00:00.000Z',
      updatedAt: now,
    },
    // Young Makers members (interest_hobby)
    {
      id: 'cm-004',
      communityId: 'community-002',
      userId: 'demo-user-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: true,
      joinedAt: '2025-07-20T00:00:00.000Z',
      createdAt: '2025-07-20T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-005',
      communityId: 'community-002',
      userId: 'staff-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-07-15T00:00:00.000Z',
      createdAt: '2025-07-15T00:00:00.000Z',
      updatedAt: now,
    },
    // Thompson Family members (family)
    {
      id: 'cm-006',
      communityId: 'community-003',
      userId: 'parent-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-08-20T00:00:00.000Z',
      createdAt: '2025-08-20T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-007',
      communityId: 'community-003',
      userId: 'student-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: true,
      joinedAt: '2025-08-21T00:00:00.000Z',
      createdAt: '2025-08-21T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-008',
      communityId: 'community-003',
      userId: 'demo-user-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: true,
      joinedAt: '2025-08-25T00:00:00.000Z',
      createdAt: '2025-08-25T00:00:00.000Z',
      updatedAt: now,
    },
    // Adventure Buddies members (trusted_friends)
    {
      id: 'cm-017',
      communityId: 'community-004',
      userId: 'demo-user-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-09-01T00:00:00.000Z',
      createdAt: '2025-09-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-018',
      communityId: 'community-004',
      userId: 'parent-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: true,
      joinedAt: '2025-09-02T00:00:00.000Z',
      createdAt: '2025-09-02T00:00:00.000Z',
      updatedAt: now,
    },
    // Weekend Warriors members (casual_friends)
    {
      id: 'cm-019',
      communityId: 'community-005',
      userId: 'demo-user-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-09-15T00:00:00.000Z',
      createdAt: '2025-09-15T00:00:00.000Z',
      updatedAt: now,
    },
    // School community memberships (Full School — institutional)
    {
      id: 'cm-009',
      communityId: 'school-community-001',
      userId: 'school-admin-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-010',
      communityId: 'school-community-001',
      userId: 'teacher-001',
      role: 'admin',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-011',
      communityId: 'school-community-001',
      userId: 'student-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-012',
      communityId: 'school-community-001',
      userId: 'parent-001',
      role: 'member',
      status: 'active',
      canPost: false,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    // Tier 1 student in Full School community (roster-managed auto-membership)
    {
      id: 'cm-023',
      communityId: 'school-community-001',
      userId: 'student-tier1-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-15T00:00:00.000Z',
      createdAt: '2025-08-15T00:00:00.000Z',
      updatedAt: now,
    },
    // Grade community memberships (5th Grade — institutional)
    {
      id: 'cm-013',
      communityId: 'school-community-002',
      userId: 'teacher-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-014',
      communityId: 'school-community-002',
      userId: 'student-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    // Tier 1 student in Grade community (roster-managed auto-membership)
    {
      id: 'cm-024',
      communityId: 'school-community-002',
      userId: 'student-tier1-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-15T00:00:00.000Z',
      createdAt: '2025-08-15T00:00:00.000Z',
      updatedAt: now,
    },
    // Class community memberships (Class 5A — institutional)
    {
      id: 'cm-015',
      communityId: 'school-community-003',
      userId: 'teacher-001',
      role: 'owner',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-016',
      communityId: 'school-community-003',
      userId: 'student-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    // Tier 1 student in Class 5A (roster-managed auto-membership)
    {
      id: 'cm-025',
      communityId: 'school-community-003',
      userId: 'student-tier1-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-15T00:00:00.000Z',
      createdAt: '2025-08-15T00:00:00.000Z',
      updatedAt: now,
    },
    // Mock students in Class 5A for assignment progress display
    {
      id: 'cm-017',
      communityId: 'school-community-003',
      userId: 'mock-student-002',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-018',
      communityId: 'school-community-003',
      userId: 'mock-student-003',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-019',
      communityId: 'school-community-003',
      userId: 'mock-student-004',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-020',
      communityId: 'school-community-003',
      userId: 'mock-student-005',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    {
      id: 'cm-021',
      communityId: 'school-community-003',
      userId: 'mock-student-006',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
    // Add demo user to Class 5A so they can test class assignment features
    {
      id: 'cm-022',
      communityId: 'school-community-003',
      userId: 'demo-user-001',
      role: 'member',
      status: 'active',
      canPost: true,
      canModerate: false,
      canInvite: false,
      joinedAt: '2025-08-01T00:00:00.000Z',
      createdAt: '2025-08-01T00:00:00.000Z',
      updatedAt: now,
    },
  ]

  for (const membership of memberships) {
    mockDb.insertWithId('communityMemberships', membership as CommunityMembership)
  }

  // --- Feed Items ---
  const feedItems: Array<Partial<FeedItem>> = [
    // Nature Explorers feed
    {
      id: 'feed-001',
      communityId: 'community-001',
      authorUserId: 'staff-001',
      type: 'announcement',
      textContent: 'Welcome to Nature Explorers! This is a space to share your outdoor adventures, nature photography, and gardening wins. Be kind, be curious, and get outside!',
      isPinned: true,
      isAnnouncement: true,
      likeCount: 12,
      commentCount: 3,
      createdAt: '2025-06-01T00:00:00.000Z',
      updatedAt: '2025-06-01T00:00:00.000Z',
    },
    {
      id: 'feed-002',
      communityId: 'community-001',
      authorUserId: 'demo-user-001',
      type: 'track_record',
      textContent: 'Just finished building my birdhouse! A family of wrens already moved in after just two weeks. So rewarding to see it in use!',
      challengeId: 'challenge-001',
      isPinned: false,
      isAnnouncement: false,
      likeCount: 8,
      commentCount: 4,
      createdAt: threeDaysAgo,
      updatedAt: threeDaysAgo,
    },
    {
      id: 'feed-003',
      communityId: 'community-001',
      authorUserId: 'parent-001',
      type: 'challenge_share',
      textContent: 'Has anyone tried the Plant a Vegetable Garden challenge? Looking for tips on what to plant this time of year.',
      challengeId: 'challenge-005',
      isPinned: false,
      isAnnouncement: false,
      likeCount: 5,
      commentCount: 2,
      createdAt: oneDayAgo,
      updatedAt: oneDayAgo,
    },
    {
      id: 'feed-004',
      communityId: 'community-001',
      authorUserId: 'demo-user-001',
      type: 'discussion',
      textContent: 'What are your favorite local hiking trails? I just discovered a great one near the river — perfect for the Hike a New Trail challenge!',
      isPinned: false,
      isAnnouncement: false,
      likeCount: 6,
      commentCount: 3,
      createdAt: twoHoursAgo,
      updatedAt: twoHoursAgo,
    },
    // Young Makers feed
    {
      id: 'feed-005',
      communityId: 'community-002',
      authorUserId: 'staff-001',
      type: 'announcement',
      textContent: 'Welcome to Young Makers! Share your builds, ask for help, and get inspired. Whether you are into woodworking, electronics, or crafts — you belong here.',
      isPinned: true,
      isAnnouncement: true,
      likeCount: 9,
      commentCount: 1,
      createdAt: '2025-07-15T00:00:00.000Z',
      updatedAt: '2025-07-15T00:00:00.000Z',
    },
    {
      id: 'feed-006',
      communityId: 'community-002',
      authorUserId: 'demo-user-001',
      type: 'milestone',
      textContent: 'Just hit my 5th completed building challenge! Earned the Master Builder badge!',
      isPinned: false,
      isAnnouncement: false,
      likeCount: 14,
      commentCount: 5,
      createdAt: oneWeekAgo,
      updatedAt: oneWeekAgo,
    },
    // Lincoln Elementary feed
    {
      id: 'feed-007',
      communityId: 'community-003',
      authorUserId: 'school-admin-001',
      type: 'announcement',
      textContent: 'Welcome back to a new school year! We have exciting challenges lined up for this semester. Check My Challenges for your class assignments.',
      isPinned: true,
      isAnnouncement: true,
      likeCount: 7,
      commentCount: 2,
      createdAt: '2025-09-01T00:00:00.000Z',
      updatedAt: '2025-09-01T00:00:00.000Z',
    },
    {
      id: 'feed-008',
      communityId: 'community-003',
      authorUserId: 'student-001',
      type: 'track_record',
      textContent: 'I built a terrarium for the science challenge! My moss is already growing. This was so cool!',
      challengeId: 'challenge-009',
      isPinned: false,
      isAnnouncement: false,
      likeCount: 11,
      commentCount: 3,
      createdAt: oneDayAgo,
      updatedAt: oneDayAgo,
    },
  ]

  for (const item of feedItems) {
    mockDb.insertWithId('communityFeed', item as FeedItem)
  }

  // --- Feed Comments ---
  const comments: Array<Partial<FeedComment>> = [
    {
      id: 'fc-001',
      feedItemId: 'feed-002',
      authorUserId: 'parent-001',
      content: 'This looks amazing! How long did it take to attract the birds?',
      createdAt: threeDaysAgo,
      updatedAt: threeDaysAgo,
    },
    {
      id: 'fc-002',
      feedItemId: 'feed-002',
      authorUserId: 'demo-user-001',
      content: 'About two weeks! I put some seeds near the entrance to attract them at first.',
      createdAt: threeDaysAgo,
      updatedAt: threeDaysAgo,
    },
    {
      id: 'fc-003',
      feedItemId: 'feed-003',
      authorUserId: 'demo-user-001',
      content: 'I did! Tomatoes and herbs are great for beginners. Start with cherry tomatoes — they are very forgiving.',
      createdAt: oneDayAgo,
      updatedAt: oneDayAgo,
    },
    {
      id: 'fc-004',
      feedItemId: 'feed-006',
      authorUserId: 'staff-001',
      content: 'Congratulations on the Master Builder badge! Keep up the amazing work!',
      createdAt: oneWeekAgo,
      updatedAt: oneWeekAgo,
    },
    {
      id: 'fc-005',
      feedItemId: 'feed-008',
      authorUserId: 'teacher-001',
      content: 'Great job, Alex! Your terrarium looks really well layered. Can you tell the class about it tomorrow?',
      createdAt: oneDayAgo,
      updatedAt: oneDayAgo,
    },
  ]

  for (const comment of comments) {
    mockDb.insertWithId('feedComments', comment as FeedComment)
  }
}

/**
 * Seed gamification data: badges, user badges, and XP events
 */
function seedGamification(): void {
  const now = timestamp()
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
  const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()

  // --- Badges ---
  const badges: Array<Partial<Badge>> = [
    {
      id: 'badge-001',
      name: 'First Steps',
      description: 'Complete your first challenge',
      category: 'milestone',
      rarity: 'common',
      criteria: { type: 'challenge_count', value: 1 },
      xpBonus: 10,
      displayOrder: 1,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-002',
      name: 'Trailblazer',
      description: 'Complete 5 challenges',
      category: 'milestone',
      rarity: 'uncommon',
      criteria: { type: 'challenge_count', value: 5 },
      xpBonus: 25,
      displayOrder: 2,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-003',
      name: 'Nature Lover',
      description: 'Complete 3 challenges in the Outdoor & Nature category',
      category: 'category_mastery',
      rarity: 'uncommon',
      criteria: { type: 'category_count', categoryId: 'outdoor-nature', value: 3 },
      xpBonus: 25,
      displayOrder: 3,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-004',
      name: 'Master Builder',
      description: 'Complete 5 challenges in the Building & Making category',
      category: 'category_mastery',
      rarity: 'rare',
      criteria: { type: 'category_count', categoryId: 'building-making', value: 5 },
      xpBonus: 50,
      displayOrder: 4,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-005',
      name: 'Consistency King',
      description: 'Maintain a 7-day activity streak',
      category: 'streak',
      rarity: 'rare',
      criteria: { type: 'streak', value: 7 },
      xpBonus: 50,
      displayOrder: 5,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-006',
      name: 'Social Butterfly',
      description: 'Make 5 posts in community feeds',
      category: 'cooperation',
      rarity: 'common',
      criteria: { type: 'special', value: 5 },
      xpBonus: 10,
      displayOrder: 6,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-007',
      name: 'Helping Hand',
      description: 'Complete 3 challenges in the Community & Service category',
      category: 'category_mastery',
      rarity: 'uncommon',
      criteria: { type: 'category_count', categoryId: 'community-service', value: 3 },
      xpBonus: 25,
      displayOrder: 7,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-008',
      name: 'Renaissance Explorer',
      description: 'Complete challenges in 5 different categories',
      category: 'milestone',
      rarity: 'epic',
      criteria: { type: 'special', value: 5 },
      xpBonus: 100,
      displayOrder: 8,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-009',
      name: 'Verification Pro',
      description: 'Have 10 track records verified',
      category: 'cooperation',
      rarity: 'rare',
      criteria: { type: 'verification_count', value: 10 },
      xpBonus: 50,
      displayOrder: 9,
      isHidden: false,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'badge-010',
      name: 'Legendary Explorer',
      description: 'Complete 25 challenges',
      category: 'milestone',
      rarity: 'legendary',
      criteria: { type: 'challenge_count', value: 25 },
      xpBonus: 250,
      displayOrder: 10,
      isHidden: true,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    },
  ]

  for (const badge of badges) {
    mockDb.insertWithId('badges', badge as Badge)
  }

  // --- User Badges (demo user has earned 4) ---
  const userBadges: Array<Partial<UserBadge>> = [
    {
      id: 'ub-001',
      userId: 'demo-user-001',
      badgeId: 'badge-001',
      earnedAt: oneMonthAgo,
      metadata: { challengeId: 'challenge-001' },
      createdAt: oneMonthAgo,
      updatedAt: oneMonthAgo,
    },
    {
      id: 'ub-002',
      userId: 'demo-user-001',
      badgeId: 'badge-003',
      earnedAt: twoWeeksAgo,
      metadata: { categorySlug: 'outdoor-nature' },
      createdAt: twoWeeksAgo,
      updatedAt: twoWeeksAgo,
    },
    {
      id: 'ub-003',
      userId: 'demo-user-001',
      badgeId: 'badge-006',
      earnedAt: oneWeekAgo,
      metadata: { postCount: 5 },
      createdAt: oneWeekAgo,
      updatedAt: oneWeekAgo,
    },
    {
      id: 'ub-004',
      userId: 'demo-user-001',
      badgeId: 'badge-002',
      earnedAt: threeDaysAgo,
      metadata: { challengeCount: 5 },
      createdAt: threeDaysAgo,
      updatedAt: threeDaysAgo,
    },
  ]

  for (const ub of userBadges) {
    mockDb.insertWithId('userBadges', ub as UserBadge)
  }

  // --- XP Events (demo user activity history) ---
  const xpEvents: Array<Partial<XPEvent>> = [
    {
      id: 'xp-001',
      userId: 'demo-user-001',
      eventType: 'challenge_completed',
      amount: 100,
      metadata: { challengeId: 'challenge-001', challengeTitle: 'Build a Birdhouse' },
      createdAt: oneMonthAgo,
      updatedAt: oneMonthAgo,
    },
    {
      id: 'xp-002',
      userId: 'demo-user-001',
      eventType: 'badge_earned',
      amount: 10,
      metadata: { badgeId: 'badge-001', badgeName: 'First Steps' },
      createdAt: oneMonthAgo,
      updatedAt: oneMonthAgo,
    },
    {
      id: 'xp-003',
      userId: 'demo-user-001',
      eventType: 'community_post',
      amount: 3,
      metadata: { communityId: 'community-001' },
      createdAt: twoWeeksAgo,
      updatedAt: twoWeeksAgo,
    },
    {
      id: 'xp-004',
      userId: 'demo-user-001',
      eventType: 'badge_earned',
      amount: 25,
      metadata: { badgeId: 'badge-003', badgeName: 'Nature Lover' },
      createdAt: twoWeeksAgo,
      updatedAt: twoWeeksAgo,
    },
    {
      id: 'xp-005',
      userId: 'demo-user-001',
      eventType: 'track_entry_added',
      amount: 5,
      metadata: { challengeId: 'challenge-005', challengeTitle: 'Plant a Vegetable Garden' },
      createdAt: oneWeekAgo,
      updatedAt: oneWeekAgo,
    },
    {
      id: 'xp-006',
      userId: 'demo-user-001',
      eventType: 'first_of_day',
      amount: 5,
      metadata: { description: 'Daily activity bonus' },
      createdAt: oneWeekAgo,
      updatedAt: oneWeekAgo,
    },
    {
      id: 'xp-007',
      userId: 'demo-user-001',
      eventType: 'badge_earned',
      amount: 10,
      metadata: { badgeId: 'badge-006', badgeName: 'Social Butterfly' },
      createdAt: oneWeekAgo,
      updatedAt: oneWeekAgo,
    },
    {
      id: 'xp-008',
      userId: 'demo-user-001',
      eventType: 'streak_bonus',
      amount: 15,
      metadata: { streakDays: 5 },
      createdAt: threeDaysAgo,
      updatedAt: threeDaysAgo,
    },
    {
      id: 'xp-009',
      userId: 'demo-user-001',
      eventType: 'badge_earned',
      amount: 25,
      metadata: { badgeId: 'badge-002', badgeName: 'Trailblazer' },
      createdAt: threeDaysAgo,
      updatedAt: threeDaysAgo,
    },
    {
      id: 'xp-010',
      userId: 'demo-user-001',
      eventType: 'milestone_completed',
      amount: 10,
      metadata: { challengeId: 'challenge-002', milestoneName: 'Research a cuisine' },
      createdAt: now,
      updatedAt: now,
    },
  ]

  for (const xp of xpEvents) {
    mockDb.insertWithId('xpEvents', xp as XPEvent)
  }
}

/**
 * Seed gift data for demo user
 */
function seedGifts(): void {
  const now = timestamp()
  const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
  const inOneWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
  const expired = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()

  const gifts: Array<Partial<Gift>> = [
    {
      id: 'gift-001',
      senderUserId: 'parent-001',
      recipientUserId: 'demo-user-001',
      challengeId: 'challenge-006',
      status: 'pending',
      personalMessage: 'I thought you might enjoy learning the ukulele! Have fun with it.',
      sentAt: twoDaysAgo,
      expiresAt: inOneWeek,
      createdAt: twoDaysAgo,
      updatedAt: twoDaysAgo,
    },
    {
      id: 'gift-002',
      senderUserId: 'demo-user-001',
      recipientUserId: 'student-001',
      challengeId: 'challenge-010',
      status: 'accepted',
      personalMessage: 'Origami is so relaxing. I think you will love it!',
      sentAt: twoWeeksAgo,
      acceptedAt: oneWeekAgo,
      expiresAt: expired,
      createdAt: twoWeeksAgo,
      updatedAt: oneWeekAgo,
    },
    {
      id: 'gift-003',
      senderUserId: 'demo-user-001',
      recipientEmail: 'friend@example.com',
      challengeId: 'challenge-014',
      status: 'pending',
      personalMessage: 'Check out this model rocket kit — perfect for your next weekend project!',
      sentAt: twoDaysAgo,
      expiresAt: inOneWeek,
      createdAt: twoDaysAgo,
      updatedAt: twoDaysAgo,
    },
    {
      id: 'gift-004',
      senderUserId: 'staff-001',
      recipientUserId: 'demo-user-001',
      challengeId: 'challenge-024',
      status: 'expired',
      personalMessage: 'As a thank you for being an active community member!',
      sentAt: twoWeeksAgo,
      expiresAt: expired,
      createdAt: twoWeeksAgo,
      updatedAt: now,
    },
  ]

  for (const gift of gifts) {
    mockDb.insertWithId('gifts', gift as Gift)
  }
}

/**
 * Seed community assignments for class communities
 * Ensures every class community has an active assigned challenge
 */
function seedCommunityAssignments(): void {
  const now = timestamp()
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  const oneWeekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()

  // --- Community Assignments ---
  // Assign challenges to class communities so the feature is always visible
  const assignments: Array<Partial<CommunityAssignment>> = [
    {
      id: 'assignment-001',
      communityId: 'school-community-003', // Class 5A
      challengeId: 'challenge-009', // Build a Terrarium
      assignedByUserId: 'teacher-001',
      dueDate: oneWeekFromNow,
      status: 'active',
      assignmentNote: 'Great science project for this week! Take photos of your terrarium as it grows. We will discuss the water cycle in class on Friday.',
      totalStudents: 24,
      completedCount: 3,
      inProgressCount: 8,
      submittedCount: 2,
      createdAt: threeDaysAgo,
      updatedAt: now,
    },
  ]

  for (const assignment of assignments) {
    mockDb.insertWithId('communityAssignments', assignment as CommunityAssignment)
  }

  // --- Student Assignment Progress ---
  // Track individual student progress on assignments
  const studentProgress: Array<Partial<CommunityStudentProgress>> = [
    // Student-001 is in progress on the terrarium challenge
    {
      id: 'sap-001',
      assignmentId: 'assignment-001',
      userId: 'student-001',
      userChallengeId: undefined, // Would be linked when they accept
      status: 'in_progress',
      progressPercent: 45,
      startedAt: threeDaysAgo,
    },
    // Mock additional students for realistic display
    {
      id: 'sap-002',
      assignmentId: 'assignment-001',
      userId: 'mock-student-002',
      status: 'completed',
      progressPercent: 100,
      startedAt: threeDaysAgo,
      completedAt: now,
    },
    {
      id: 'sap-003',
      assignmentId: 'assignment-001',
      userId: 'mock-student-003',
      status: 'submitted',
      progressPercent: 100,
      startedAt: threeDaysAgo,
      submittedAt: now,
    },
    {
      id: 'sap-004',
      assignmentId: 'assignment-001',
      userId: 'mock-student-004',
      status: 'in_progress',
      progressPercent: 60,
      startedAt: threeDaysAgo,
    },
    {
      id: 'sap-005',
      assignmentId: 'assignment-001',
      userId: 'mock-student-005',
      status: 'not_started',
      progressPercent: 0,
    },
    {
      id: 'sap-006',
      assignmentId: 'assignment-001',
      userId: 'mock-student-006',
      status: 'in_progress',
      progressPercent: 25,
      startedAt: now,
    },
  ]

  for (const progress of studentProgress) {
    mockDb.insertWithId('studentAssignmentProgress', progress as CommunityStudentProgress)
  }

  // Add a past (completed) assignment for history
  const pastAssignment: Partial<CommunityAssignment> = {
    id: 'assignment-002',
    communityId: 'school-community-003', // Class 5A
    challengeId: 'challenge-010', // Learn Basic Origami
    assignedByUserId: 'teacher-001',
    dueDate: threeDaysAgo,
    status: 'completed',
    assignmentNote: 'Fun art project! Bring your best origami to show the class.',
    totalStudents: 24,
    completedCount: 20,
    inProgressCount: 0,
    submittedCount: 0,
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: threeDaysAgo,
  }
  mockDb.insertWithId('communityAssignments', pastAssignment as CommunityAssignment)
}

/**
 * Seed rejection reason templates for track record verification
 */
function seedRejectionReasonTemplates(): void {
  const templates: Array<Omit<RejectionReasonTemplate, 'createdAt' | 'updatedAt'>> = [
    {
      id: 'reject-reason-001',
      title: 'Incomplete Documentation',
      description: 'Track record does not show full challenge completion. Please add more entries documenting your progress.',
      isActive: true,
      displayOrder: 1,
    },
    {
      id: 'reject-reason-002',
      title: 'Unclear Evidence',
      description: 'Photos or descriptions do not clearly demonstrate the challenge was completed. Please provide clearer documentation.',
      isActive: true,
      displayOrder: 2,
    },
    {
      id: 'reject-reason-003',
      title: 'Missing Required Elements',
      description: 'One or more required verification elements are missing. Please review the challenge requirements.',
      isActive: true,
      displayOrder: 3,
    },
    {
      id: 'reject-reason-004',
      title: 'Content Quality',
      description: 'Images are too blurry or text is too brief to verify completion. Please provide clearer content.',
      isActive: true,
      displayOrder: 4,
    },
    {
      id: 'reject-reason-005',
      title: 'Does Not Match Requirements',
      description: 'The submitted work does not match the challenge requirements. Please review and resubmit.',
      isActive: true,
      displayOrder: 5,
    },
  ]

  for (const template of templates) {
    mockDb.insertWithId('rejectionReasonTemplates', {
      ...template,
      createdAt: timestamp(),
      updatedAt: timestamp(),
    } as RejectionReasonTemplate)
  }
}

// ============================================================================
// Learning Paths Seeding
// ============================================================================

function seedLearningPaths(): void {
  const now = timestamp()

  const learningPaths = [
    {
      id: 'path-001',
      title: 'Young Scientist Journey',
      description: 'Explore the scientific method through hands-on experiments and discoveries.',
      coverImageUrl: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800',
      challengeIds: ['ch-001', 'ch-006', 'ch-009'],
      checkpoints: [
        { title: 'First Experiment Complete', description: 'You\'ve completed your first scientific experiment!', afterChallengeIndex: 0, bonusXp: 50 },
        { title: 'Budding Scientist', description: 'You\'re developing real scientific skills!', afterChallengeIndex: 2, bonusXp: 100 },
      ],
      difficulty: 'beginner',
      estimatedDuration: '2 weeks',
      status: 'published',
      category: 'learn-discover',
      creatorId: 'admin-001',
      enrollmentCount: 156,
      completionCount: 42,
      pathBadgeId: 'badge-path-scientist',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'path-002',
      title: 'Creative Maker Path',
      description: 'Build, create, and express yourself through hands-on making projects.',
      coverImageUrl: 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=800',
      challengeIds: ['ch-003', 'ch-005', 'ch-013', 'ch-015'],
      checkpoints: [
        { title: 'First Creation', description: 'You\'ve made your first project!', afterChallengeIndex: 0, bonusXp: 50 },
        { title: 'Skilled Maker', description: 'You\'re becoming a skilled creator!', afterChallengeIndex: 2, bonusXp: 75 },
        { title: 'Master Maker', description: 'You\'ve completed the Maker Path!', afterChallengeIndex: 3, bonusXp: 150 },
      ],
      difficulty: 'intermediate',
      estimatedDuration: '3 weeks',
      status: 'published',
      category: 'build-engineer',
      creatorId: 'admin-001',
      enrollmentCount: 89,
      completionCount: 23,
      pathBadgeId: 'badge-path-maker',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'path-003',
      title: 'Nature Explorer',
      description: 'Connect with the natural world through outdoor adventures and discoveries.',
      coverImageUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800',
      challengeIds: ['ch-002', 'ch-008', 'ch-010'],
      checkpoints: [
        { title: 'Outdoor Adventurer', description: 'You\'ve started exploring nature!', afterChallengeIndex: 0, bonusXp: 50 },
        { title: 'Nature Guardian', description: 'You\'re a true nature explorer!', afterChallengeIndex: 2, bonusXp: 100 },
      ],
      difficulty: 'beginner',
      estimatedDuration: '2 weeks',
      status: 'published',
      category: 'care-cultivate',
      creatorId: 'admin-001',
      enrollmentCount: 234,
      completionCount: 78,
      pathBadgeId: 'badge-path-nature',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'path-004',
      title: 'Community Leader',
      description: 'Learn to connect, serve, and make a positive impact in your community.',
      coverImageUrl: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800',
      challengeIds: ['ch-004', 'ch-007', 'ch-011', 'ch-012'],
      checkpoints: [
        { title: 'Helper', description: 'You\'ve started helping others!', afterChallengeIndex: 0, bonusXp: 50 },
        { title: 'Community Builder', description: 'You\'re making a real difference!', afterChallengeIndex: 2, bonusXp: 75 },
        { title: 'Community Champion', description: 'You\'re a true community leader!', afterChallengeIndex: 3, bonusXp: 150 },
      ],
      difficulty: 'intermediate',
      estimatedDuration: '4 weeks',
      status: 'published',
      category: 'connect-serve',
      creatorId: 'admin-001',
      enrollmentCount: 67,
      completionCount: 15,
      pathBadgeId: 'badge-path-leader',
      createdAt: now,
      updatedAt: now,
    },
  ]

  for (const path of learningPaths) {
    mockDb.insertWithId('learningPaths', path)
  }
}

// ============================================================================
// Portfolios Seeding
// ============================================================================

function seedPortfolios(): void {
  const now = timestamp()

  const portfolios = [
    {
      id: 'portfolio-001',
      userId: 'demo-user-001',
      title: 'My Creative Journey',
      introduction: 'A collection of my favorite challenges and projects from the past year.',
      sections: [
        { title: 'Art & Creativity', trackRecordIds: ['tr-001'], description: 'Exploring my artistic side' },
        { title: 'Science Experiments', trackRecordIds: ['tr-002'], description: 'Hands-on learning adventures' },
      ],
      templateId: 'template-classic',
      status: 'draft',
      shareSettings: {
        linkType: 'private',
        password: null,
        expiresAt: null,
      },
      viewCount: 0,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'portfolio-002',
      userId: 'user-002',
      title: 'Summer Adventures 2024',
      introduction: 'All the amazing things I did this summer!',
      sections: [
        { title: 'Outdoor Fun', trackRecordIds: [], description: 'Nature and adventure' },
      ],
      templateId: 'template-modern',
      status: 'published',
      shareSettings: {
        linkType: 'public',
        password: null,
        expiresAt: null,
      },
      viewCount: 24,
      createdAt: now,
      updatedAt: now,
    },
  ]

  for (const portfolio of portfolios) {
    mockDb.insertWithId('portfolios', portfolio)
  }
}

// ============================================================================
// Events Seeding
// ============================================================================

function seedEvents(): void {
  const now = new Date()

  const events = [
    {
      id: 'event-001',
      challengeId: 'ch-001',
      title: 'Saturday Morning Bird Walk',
      description: 'Join us for a guided bird watching tour at Riverside Park. All ages welcome!',
      startDate: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000).toISOString(),
      location: 'Riverside Park',
      isOnline: false,
      capacity: 20,
      registeredCount: 12,
      registrationDeadline: new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString(),
      price: 0,
      currency: 'USD',
      vendorId: 'vendor-001',
      status: 'upcoming',
      createdAt: timestamp(),
      updatedAt: timestamp(),
    },
    {
      id: 'event-002',
      challengeId: 'ch-005',
      title: 'Beginner Bread Baking Workshop',
      description: 'Learn to bake artisan bread from scratch. Take home your creations!',
      startDate: new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000 + 3 * 60 * 60 * 1000).toISOString(),
      location: 'Community Kitchen, 123 Main St',
      isOnline: false,
      capacity: 15,
      registeredCount: 15,
      price: 25,
      currency: 'USD',
      vendorId: 'vendor-002',
      status: 'upcoming',
      createdAt: timestamp(),
      updatedAt: timestamp(),
    },
    {
      id: 'event-003',
      challengeId: 'ch-010',
      title: 'Online Watercolor Session',
      description: 'Virtual watercolor painting class for beginners. Materials list sent after registration.',
      startDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000 + 1.5 * 60 * 60 * 1000).toISOString(),
      location: 'Zoom (link sent after registration)',
      isOnline: true,
      capacity: 30,
      registeredCount: 18,
      price: 15,
      currency: 'USD',
      vendorId: 'vendor-003',
      status: 'upcoming',
      createdAt: timestamp(),
      updatedAt: timestamp(),
    },
    {
      id: 'event-004',
      challengeId: 'ch-013',
      title: 'Science Fair Prep Day',
      description: 'Hands-on science experiments and project planning help.',
      startDate: new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000 + 4 * 60 * 60 * 1000).toISOString(),
      location: 'Discovery Center',
      isOnline: false,
      capacity: 25,
      registeredCount: 8,
      price: 0,
      currency: 'USD',
      vendorId: 'vendor-001',
      status: 'upcoming',
      createdAt: timestamp(),
      updatedAt: timestamp(),
    },
    {
      id: 'event-005',
      challengeId: 'ch-002',
      title: 'Sunset Photography Walk',
      description: 'Capture golden hour photos in the park. Bring your camera or phone!',
      startDate: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000 + 1.5 * 60 * 60 * 1000).toISOString(),
      location: 'Hilltop Park',
      isOnline: false,
      capacity: 12,
      registeredCount: 7,
      price: 10,
      currency: 'USD',
      vendorId: 'vendor-003',
      status: 'upcoming',
      createdAt: timestamp(),
      updatedAt: timestamp(),
    },
    {
      id: 'event-006',
      challengeId: 'ch-008',
      title: 'Family Gardening Day',
      description: 'Learn to start a vegetable garden. Seeds and supplies provided!',
      startDate: new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000 + 3 * 60 * 60 * 1000).toISOString(),
      location: 'Community Garden',
      isOnline: false,
      capacity: 20,
      registeredCount: 5,
      price: 5,
      currency: 'USD',
      vendorId: 'vendor-002',
      status: 'upcoming',
      createdAt: timestamp(),
      updatedAt: timestamp(),
    },
  ]

  for (const event of events) {
    mockDb.insertWithId('events', event)
  }
}

// ============================================================================
// Reflection Prompts Seeding
// ============================================================================

function seedReflectionPrompts(): void {
  const now = timestamp()

  const prompts = [
    {
      id: 'prompt-001',
      question: 'How did this challenge make you feel?',
      type: 'emoji_scale',
      options: ['😞', '😐', '🙂', '😊', '🤩'],
      selCompetency: 'self_awareness',
      ageGroup: 'all',
      isRequired: true,
      displayOrder: 1,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'prompt-002',
      question: 'What was the most interesting thing you learned?',
      type: 'open_text_short',
      options: null,
      selCompetency: 'self_awareness',
      ageGroup: 'all',
      isRequired: false,
      displayOrder: 2,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'prompt-003',
      question: 'Would you recommend this challenge to a friend?',
      type: 'numeric_scale',
      options: ['1', '2', '3', '4', '5'],
      selCompetency: 'social_awareness',
      ageGroup: 'all',
      isRequired: true,
      displayOrder: 3,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'prompt-004',
      question: 'What was the hardest part of this challenge?',
      type: 'multiple_choice_single',
      options: ['Getting started', 'Staying focused', 'Finding materials', 'Nothing was hard', 'Other'],
      selCompetency: 'self_management',
      ageGroup: '13_plus',
      isRequired: false,
      displayOrder: 4,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'prompt-005',
      question: 'Did you work with anyone else on this challenge?',
      type: 'multiple_choice_single',
      options: ['By myself', 'With family', 'With friends', 'With classmates'],
      selCompetency: 'relationship_skills',
      ageGroup: 'all',
      isRequired: false,
      displayOrder: 5,
      createdAt: now,
      updatedAt: now,
    },
  ]

  for (const prompt of prompts) {
    mockDb.insertWithId('reflectionPrompts', prompt)
  }
}

// ============================================================================
// Reports Seeding (for admin moderation view)
// ============================================================================

function seedReports(): void {
  const now = timestamp()

  const reports = [
    {
      id: 'report-001',
      reporterId: 'user-002',
      contentType: 'track_record',
      contentId: 'tr-sample-001',
      reason: 'inappropriate',
      description: 'This content contains language that may not be appropriate for younger users.',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'report-002',
      reporterId: 'user-003',
      contentType: 'community',
      contentId: 'community-sample-001',
      reason: 'spam',
      description: 'This community is posting promotional content repeatedly.',
      status: 'reviewed',
      reviewedBy: 'admin-001',
      reviewedAt: now,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'report-003',
      reporterId: 'demo-user-001',
      contentType: 'feed_post',
      contentId: 'post-sample-001',
      reason: 'misinformation',
      description: 'This post contains inaccurate information about the challenge requirements.',
      status: 'action_taken',
      reviewedBy: 'admin-001',
      reviewedAt: now,
      actionTaken: 'Content removed and user warned',
      createdAt: now,
      updatedAt: now,
    },
  ]

  for (const report of reports) {
    mockDb.insertWithId('reports', report)
  }
}

/**
 * Run all seed functions
 */
function seedCommunityGoals(): void {
  const now = new Date().toISOString()
  const goals: Array<Omit<CommunityGoal, 'createdAt' | 'updatedAt'>> = [
    {
      id: 'goal-001',
      communityId: 'community-001',
      title: '100 Challenges Completed',
      description: 'Together, let\'s complete 100 challenges as a community this month!',
      contributionType: 'challenge_completion',
      targetAmount: 100,
      currentAmount: 42,
      startDate: '2025-09-01T00:00:00.000Z',
      endDate: '2025-09-30T23:59:59.000Z',
      status: 'active',
      participantCount: 18,
      rewardXp: 500,
      milestones: [
        { id: 'gm-001', title: '25 Challenges', targetAmount: 25, rewardXp: 50, reached: true, reachedAt: '2025-09-10T00:00:00.000Z' },
        { id: 'gm-002', title: '50 Challenges', targetAmount: 50, rewardXp: 100, reached: false },
        { id: 'gm-003', title: '75 Challenges', targetAmount: 75, rewardXp: 150, reached: false },
        { id: 'gm-004', title: '100 Challenges', targetAmount: 100, rewardXp: 200, reached: false },
      ],
    },
    {
      id: 'goal-002',
      communityId: 'community-001',
      title: 'Earn 5,000 XP Together',
      description: 'A collective XP goal — every member\'s progress counts!',
      contributionType: 'xp_earned',
      targetAmount: 5000,
      currentAmount: 3200,
      startDate: '2025-09-01T00:00:00.000Z',
      endDate: '2025-10-31T23:59:59.000Z',
      status: 'active',
      participantCount: 24,
      rewardXp: 300,
      milestones: [
        { id: 'gm-005', title: '1,000 XP', targetAmount: 1000, rewardXp: 25, reached: true, reachedAt: '2025-09-05T00:00:00.000Z' },
        { id: 'gm-006', title: '2,500 XP', targetAmount: 2500, rewardXp: 50, reached: true, reachedAt: '2025-09-15T00:00:00.000Z' },
        { id: 'gm-007', title: '5,000 XP', targetAmount: 5000, rewardXp: 100, reached: false },
      ],
    },
    {
      id: 'goal-003',
      communityId: 'school-community-001',
      title: 'Class Challenge: 30 Track Records',
      description: 'Let\'s share 30 track records as a class before the end of the semester!',
      contributionType: 'track_records',
      targetAmount: 30,
      currentAmount: 12,
      startDate: '2025-09-01T00:00:00.000Z',
      endDate: '2025-12-20T23:59:59.000Z',
      status: 'active',
      participantCount: 15,
      rewardXp: 200,
      milestones: [
        { id: 'gm-008', title: '10 Track Records', targetAmount: 10, rewardXp: 30, reached: true, reachedAt: '2025-09-20T00:00:00.000Z' },
        { id: 'gm-009', title: '20 Track Records', targetAmount: 20, rewardXp: 60, reached: false },
        { id: 'gm-010', title: '30 Track Records', targetAmount: 30, rewardXp: 100, reached: false },
      ],
    },
    {
      id: 'goal-004',
      communityId: 'community-002',
      title: 'Summer Reading Sprint',
      description: 'Complete 50 reading-related challenges together this summer.',
      contributionType: 'challenge_completion',
      targetAmount: 50,
      currentAmount: 50,
      startDate: '2025-06-01T00:00:00.000Z',
      endDate: '2025-08-31T23:59:59.000Z',
      status: 'completed',
      participantCount: 22,
      rewardXp: 400,
      milestones: [
        { id: 'gm-011', title: '25 Challenges', targetAmount: 25, rewardXp: 75, reached: true, reachedAt: '2025-07-15T00:00:00.000Z' },
        { id: 'gm-012', title: '50 Challenges', targetAmount: 50, rewardXp: 150, reached: true, reachedAt: '2025-08-28T00:00:00.000Z' },
      ],
    },
  ]

  for (const goal of goals) {
    mockDb.insertWithId('communityGoals', {
      ...goal,
      createdAt: now,
      updatedAt: now,
    } as CommunityGoal)
  }
}

/**
 * Seed vendor profiles
 */
function seedVendors(): void {
  const craftBox: Vendor = {
    id: 'vendor-profile-001',
    userId: 'vendor-001',
    companyName: 'CraftBox',
    profile: {
      shortDescription: 'CraftBox creates hands-on learning kits and challenges that spark creativity and curiosity in learners of all ages.',
      logo: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=400&fit=crop',
      tagline: 'Hands-on learning, delivered to your door',
      website: 'https://craftbox.example.com',
      socialLinks: {
        instagram: 'https://instagram.com/craftbox',
        facebook: 'https://facebook.com/craftbox',
        youtube: 'https://youtube.com/craftbox',
        linkedin: 'https://linkedin.com/company/craftbox',
      },
    },
    contact: {
      contactName: 'Alex Chen',
      email: 'vendor@craftbox.com',
      phoneNumber: '(555) 123-4567',
    },
    billingAddress: {
      street: '456 Maker Lane',
      city: 'Portland',
      state: 'OR',
      postalCode: '97201',
      country: 'US',
    },
    virtualVenues: [],
    physicalVenues: [{
      id: 'venue-001',
      name: 'CraftBox Workshop',
      address: { street: '456 Maker Lane', city: 'Portland', state: 'OR', postalCode: '97201', country: 'US' },
    }],
    documents: [],
    approvalStatus: 'approved',
    accountStatus: 'active',
    onboardingStep: 3,
    isOnboardingComplete: true,
    createdAt: '2018-06-15T00:00:00.000Z',
    updatedAt: '2025-01-01T00:00:00.000Z',
  }

  mockDb.insertWithId('vendors', craftBox as unknown as Vendor)
}

/**
 * Seed provider feedback entries (anonymous)
 */
function seedProviderFeedback(): void {
  const now = Date.now()
  const DAY = 24 * 60 * 60 * 1000

  const entries: Array<Partial<ProviderFeedback>> = [
    {
      id: 'pf-001',
      challengeId: 'challenge-004',
      vendorId: 'vendor-001',
      rating: 5,
      feedback: 'The kit was incredibly well organized and the instructions were clear. My child loved building the robot!',
      timeToCompleteMs: 10 * DAY,
      updateCount: 6,
      challengeTitle: 'Robotics Starter Kit',
      completedAt: new Date(now - 5 * DAY).toISOString(),
    },
    {
      id: 'pf-002',
      challengeId: 'challenge-004',
      vendorId: 'vendor-001',
      rating: 4,
      feedback: 'Great challenge overall. The motor assembly step could use a bit more detail for younger kids.',
      timeToCompleteMs: 14 * DAY,
      updateCount: 8,
      challengeTitle: 'Robotics Starter Kit',
      completedAt: new Date(now - 12 * DAY).toISOString(),
    },
    {
      id: 'pf-003',
      challengeId: 'challenge-014',
      vendorId: 'vendor-001',
      rating: 5,
      feedback: 'Absolutely amazing experience! The launch day was unforgettable. Would recommend to everyone.',
      timeToCompleteMs: 7 * DAY,
      updateCount: 4,
      challengeTitle: 'Build a Model Rocket',
      completedAt: new Date(now - 3 * DAY).toISOString(),
    },
    {
      id: 'pf-004',
      challengeId: 'challenge-014',
      vendorId: 'vendor-001',
      rating: 3,
      feedback: 'Fun project but the paint took longer to dry than expected. Maybe suggest faster-drying alternatives.',
      timeToCompleteMs: 21 * DAY,
      updateCount: 5,
      challengeTitle: 'Build a Model Rocket',
      completedAt: new Date(now - 18 * DAY).toISOString(),
    },
    {
      id: 'pf-005',
      challengeId: 'challenge-024',
      vendorId: 'vendor-001',
      rating: 4,
      feedback: 'Loved the variety of techniques covered. The scented candle was a huge hit at home!',
      timeToCompleteMs: 12 * DAY,
      updateCount: 5,
      challengeTitle: 'Candle Making Workshop',
      completedAt: new Date(now - 8 * DAY).toISOString(),
    },
    {
      id: 'pf-006',
      challengeId: 'challenge-024',
      vendorId: 'vendor-001',
      rating: 5,
      feedback: 'Perfect weekend activity. Instructions were spot on and the results were beautiful.',
      timeToCompleteMs: 9 * DAY,
      updateCount: 3,
      challengeTitle: 'Candle Making Workshop',
      completedAt: new Date(now - 2 * DAY).toISOString(),
    },
  ]

  for (const entry of entries) {
    mockDb.insertWithId('providerFeedback', entry as ProviderFeedback)
  }
}

/**
 * Seed track records for school community students
 * Creates submitted/completed TRs so the teacher review flow works
 */
function seedStudentTrackRecords(): void {
  const now = timestamp()
  const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()

  // assignment-001 is for challenge-009 (Build a Terrarium) in school-community-003
  const challengeId = 'challenge-009'

  // Create UserChallenges for mock students
  const userChallenges: Array<Partial<UserChallenge>> = [
    {
      id: 'uc-school-002',
      userId: 'mock-student-002',
      challengeId,
      status: 'completed',
      source: 'class_assignment',
      communityAssignmentId: 'assignment-001',
      communityId: 'school-community-003',
      completionNumber: 1,
      verificationAttempts: 1,
      maxAttempts: 3,
      startedAt: threeDaysAgo,
      completedAt: now,
    },
    {
      id: 'uc-school-003',
      userId: 'mock-student-003',
      challengeId,
      status: 'submitted',
      source: 'class_assignment',
      communityAssignmentId: 'assignment-001',
      communityId: 'school-community-003',
      completionNumber: 1,
      verificationAttempts: 1,
      maxAttempts: 3,
      startedAt: threeDaysAgo,
      submittedAt: twoDaysAgo,
    },
    {
      id: 'uc-school-004',
      userId: 'mock-student-004',
      challengeId,
      status: 'in_progress',
      source: 'class_assignment',
      communityAssignmentId: 'assignment-001',
      communityId: 'school-community-003',
      completionNumber: 0,
      verificationAttempts: 0,
      maxAttempts: 3,
      startedAt: threeDaysAgo,
    },
    {
      id: 'uc-school-006',
      userId: 'mock-student-006',
      challengeId,
      status: 'in_progress',
      source: 'class_assignment',
      communityAssignmentId: 'assignment-001',
      communityId: 'school-community-003',
      completionNumber: 0,
      verificationAttempts: 0,
      maxAttempts: 3,
      startedAt: now,
    },
  ]

  for (const uc of userChallenges) {
    mockDb.insertWithId('userChallenges', uc as UserChallenge)
  }

  // Placeholder images (using picsum for demo)
  const img = (seed: number) => `https://picsum.photos/seed/${seed}/600/400`

  // Track Records
  const trackRecords: Array<Partial<TrackRecord>> = [
    {
      id: 'tr-school-002',
      userChallengeId: 'uc-school-002',
      userId: 'mock-student-002',
      challengeId,
      status: 'verified',
      storageUsedBytes: 0,
      sharingScope: 'communities',
      submittedAt: twoDaysAgo,
      verifiedAt: now,
      verificationAttempts: 1,
      escalated: false,
      createdAt: threeDaysAgo,
      updatedAt: now,
    },
    {
      id: 'tr-school-003',
      userChallengeId: 'uc-school-003',
      userId: 'mock-student-003',
      challengeId,
      status: 'submitted',
      storageUsedBytes: 0,
      sharingScope: 'communities',
      submittedAt: twoDaysAgo,
      verificationAttempts: 1,
      escalated: false,
      createdAt: threeDaysAgo,
      updatedAt: twoDaysAgo,
    },
    {
      id: 'tr-school-004',
      userChallengeId: 'uc-school-004',
      userId: 'mock-student-004',
      challengeId,
      status: 'draft',
      storageUsedBytes: 0,
      sharingScope: 'communities',
      verificationAttempts: 0,
      escalated: false,
      createdAt: threeDaysAgo,
      updatedAt: now,
    },
    {
      id: 'tr-school-006',
      userChallengeId: 'uc-school-006',
      userId: 'mock-student-006',
      challengeId,
      status: 'draft',
      storageUsedBytes: 0,
      sharingScope: 'communities',
      verificationAttempts: 0,
      escalated: false,
      createdAt: now,
      updatedAt: now,
    },
  ]

  for (const tr of trackRecords) {
    mockDb.insertWithId('trackRecords', tr as TrackRecord)
  }

  // Entries with images
  const entries: Array<Partial<TrackRecordEntry>> = [
    // Emma (completed) - 3 entries
    { id: 'tre-002-1', trackRecordId: 'tr-school-002', textContent: 'Found a big glass jar at the thrift store and bought some pebbles and charcoal. Also collected moss from the backyard!', displayOrder: 0, editedAfterVerification: false, thumbsUpCount: 4, commentCount: 1, createdAt: threeDaysAgo, updatedAt: threeDaysAgo },
    { id: 'tre-002-2', trackRecordId: 'tr-school-002', textContent: 'Layered everything in the jar: pebbles, charcoal, soil, then planted the fern and moss. Added a tiny figurine for fun.', displayOrder: 1, editedAfterVerification: false, thumbsUpCount: 6, commentCount: 2, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'tre-002-3', trackRecordId: 'tr-school-002', textContent: 'Day 3 and there\'s already condensation on the glass! The water cycle is working. My terrarium is self-sustaining now.', displayOrder: 2, editedAfterVerification: false, thumbsUpCount: 8, commentCount: 3, createdAt: now, updatedAt: now },
    // Michael (submitted, pending review) - 3 entries
    { id: 'tre-003-1', trackRecordId: 'tr-school-003', textContent: 'Went to the garden center with my mom and picked out a small succulent and some decorative stones.', displayOrder: 0, editedAfterVerification: false, thumbsUpCount: 2, commentCount: 0, createdAt: threeDaysAgo, updatedAt: threeDaysAgo },
    { id: 'tre-003-2', trackRecordId: 'tr-school-003', textContent: 'Used a mason jar and added the drainage layer. The succulent fits perfectly! Added some sand on top for decoration.', displayOrder: 1, editedAfterVerification: false, thumbsUpCount: 3, commentCount: 1, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'tre-003-3', trackRecordId: 'tr-school-003', textContent: 'Finished! Placed it by my window. I learned that succulents don\'t need much water so this should be easy to maintain.', displayOrder: 2, editedAfterVerification: false, thumbsUpCount: 5, commentCount: 2, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    // Sophia (in progress) - 2 entries
    { id: 'tre-004-1', trackRecordId: 'tr-school-004', textContent: 'Watched a YouTube tutorial on terrariums. I want to make a tropical one with a fern and some ivy.', displayOrder: 0, editedAfterVerification: false, thumbsUpCount: 1, commentCount: 0, createdAt: threeDaysAgo, updatedAt: threeDaysAgo },
    { id: 'tre-004-2', trackRecordId: 'tr-school-004', textContent: 'Got my container and started the base layers. Still need to get the plants this weekend!', displayOrder: 1, editedAfterVerification: false, thumbsUpCount: 2, commentCount: 0, createdAt: now, updatedAt: now },
    // Olivia (just started) - 1 entry
    { id: 'tre-006-1', trackRecordId: 'tr-school-006', textContent: 'Making a list of supplies I need. Going to use a recycled pickle jar!', displayOrder: 0, editedAfterVerification: false, thumbsUpCount: 1, commentCount: 0, createdAt: now, updatedAt: now },
  ]

  for (const entry of entries) {
    mockDb.insertWithId('trackRecordEntries', entry as TrackRecordEntry)
  }

  // Media (images for entries)
  const media: Array<Partial<TrackRecordEntryMedia>> = [
    // Emma's entries
    { id: 'trm-002-1a', entryId: 'tre-002-1', mediaType: 'image', url: img(201), displayOrder: 0, createdAt: threeDaysAgo, updatedAt: threeDaysAgo },
    { id: 'trm-002-1b', entryId: 'tre-002-1', mediaType: 'image', url: img(202), displayOrder: 1, createdAt: threeDaysAgo, updatedAt: threeDaysAgo },
    { id: 'trm-002-2a', entryId: 'tre-002-2', mediaType: 'image', url: img(203), displayOrder: 0, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'trm-002-2b', entryId: 'tre-002-2', mediaType: 'image', url: img(204), displayOrder: 1, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'trm-002-2c', entryId: 'tre-002-2', mediaType: 'image', url: img(205), displayOrder: 2, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'trm-002-3a', entryId: 'tre-002-3', mediaType: 'image', url: img(206), displayOrder: 0, createdAt: now, updatedAt: now },
    // Michael's entries
    { id: 'trm-003-1a', entryId: 'tre-003-1', mediaType: 'image', url: img(301), displayOrder: 0, createdAt: threeDaysAgo, updatedAt: threeDaysAgo },
    { id: 'trm-003-2a', entryId: 'tre-003-2', mediaType: 'image', url: img(302), displayOrder: 0, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'trm-003-2b', entryId: 'tre-003-2', mediaType: 'image', url: img(303), displayOrder: 1, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'trm-003-3a', entryId: 'tre-003-3', mediaType: 'image', url: img(304), displayOrder: 0, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    { id: 'trm-003-3b', entryId: 'tre-003-3', mediaType: 'image', url: img(305), displayOrder: 1, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
    // Sophia's entries
    { id: 'trm-004-2a', entryId: 'tre-004-2', mediaType: 'image', url: img(401), displayOrder: 0, createdAt: now, updatedAt: now },
    { id: 'trm-004-2b', entryId: 'tre-004-2', mediaType: 'image', url: img(402), displayOrder: 1, createdAt: now, updatedAt: now },
  ]

  for (const m of media) {
    mockDb.insertWithId('trackRecordMedia', m as TrackRecordEntryMedia)
  }

  // Emoji reactions on entries
  const reactions = [
    // Emma's entries get lots of reactions
    { id: 'react-001', entryId: 'tre-002-1', userId: 'teacher-001', emoji: '👏', createdAt: threeDaysAgo },
    { id: 'react-002', entryId: 'tre-002-1', userId: 'mock-student-003', emoji: '🔥', createdAt: threeDaysAgo },
    { id: 'react-003', entryId: 'tre-002-1', userId: 'mock-student-004', emoji: '👏', createdAt: threeDaysAgo },
    { id: 'react-004', entryId: 'tre-002-2', userId: 'teacher-001', emoji: '⭐', createdAt: twoDaysAgo },
    { id: 'react-005', entryId: 'tre-002-2', userId: 'mock-student-003', emoji: '❤️', createdAt: twoDaysAgo },
    { id: 'react-006', entryId: 'tre-002-2', userId: 'mock-student-006', emoji: '🔥', createdAt: twoDaysAgo },
    { id: 'react-007', entryId: 'tre-002-2', userId: 'mock-student-004', emoji: '👏', createdAt: twoDaysAgo },
    { id: 'react-008', entryId: 'tre-002-3', userId: 'teacher-001', emoji: '💪', createdAt: now },
    { id: 'react-009', entryId: 'tre-002-3', userId: 'mock-student-003', emoji: '🔥', createdAt: now },
    { id: 'react-010', entryId: 'tre-002-3', userId: 'mock-student-004', emoji: '⭐', createdAt: now },
    { id: 'react-011', entryId: 'tre-002-3', userId: 'mock-student-006', emoji: '👏', createdAt: now },
    // Michael's entries
    { id: 'react-013', entryId: 'tre-003-1', userId: 'teacher-001', emoji: '👏', createdAt: threeDaysAgo },
    { id: 'react-014', entryId: 'tre-003-2', userId: 'mock-student-002', emoji: '🔥', createdAt: twoDaysAgo },
    { id: 'react-015', entryId: 'tre-003-2', userId: 'teacher-001', emoji: '⭐', createdAt: twoDaysAgo },
    { id: 'react-016', entryId: 'tre-003-3', userId: 'mock-student-002', emoji: '💪', createdAt: twoDaysAgo },
    { id: 'react-017', entryId: 'tre-003-3', userId: 'mock-student-004', emoji: '❤️', createdAt: twoDaysAgo },
    { id: 'react-018', entryId: 'tre-003-3', userId: 'teacher-001', emoji: '👏', createdAt: twoDaysAgo },
    // Sophia's entries
    { id: 'react-019', entryId: 'tre-004-1', userId: 'teacher-001', emoji: '👏', createdAt: threeDaysAgo },
    { id: 'react-020', entryId: 'tre-004-2', userId: 'mock-student-002', emoji: '💪', createdAt: now },
    // Olivia's entry
    { id: 'react-021', entryId: 'tre-006-1', userId: 'teacher-001', emoji: '❤️', createdAt: now },
    { id: 'react-022', entryId: 'tre-006-1', userId: 'mock-student-002', emoji: '👏', createdAt: now },
  ]

  for (const r of reactions) {
    mockDb.insertWithId('entryReactions', r as any)
  }

  // Track record comments on entries
  const fifteenMinFromNow = new Date(Date.now() + 15 * 60 * 1000).toISOString()
  const comments: Array<Partial<TrackRecordComment>> = [
    // Emma's entry 1 - 1 comment
    {
      id: 'trc-001', entryId: 'tre-002-1', authorUserId: 'teacher-001', parentCommentId: undefined,
      content: 'Great start Emma! The thrift store find is very resourceful. Love the eco-friendly approach.',
      visibility: 'public', isInstructorFeedback: true, edited: false, editDeadline: threeDaysAgo,
      author: { id: 'teacher-001', displayName: 'Mr. Thompson' },
      createdAt: threeDaysAgo, updatedAt: threeDaysAgo,
    },
    // Emma's entry 2 - 2 comments (thread)
    {
      id: 'trc-002', entryId: 'tre-002-2', authorUserId: 'mock-student-003', parentCommentId: undefined,
      content: 'That figurine idea is so cute! I want to add one to mine too.',
      visibility: 'public', isInstructorFeedback: false, edited: false, editDeadline: twoDaysAgo,
      author: { id: 'mock-student-003', displayName: 'Sophia Garcia' },
      createdAt: twoDaysAgo, updatedAt: twoDaysAgo,
    },
    {
      id: 'trc-003', entryId: 'tre-002-2', authorUserId: 'teacher-001', parentCommentId: undefined,
      content: 'Excellent layering technique! The charcoal layer is important for preventing root rot. Well done.',
      visibility: 'public', isInstructorFeedback: true, edited: false, editDeadline: twoDaysAgo,
      author: { id: 'teacher-001', displayName: 'Mr. Thompson' },
      createdAt: twoDaysAgo, updatedAt: twoDaysAgo,
    },
    // Emma's entry 3 - 3 comments (with a reply thread)
    {
      id: 'trc-004', entryId: 'tre-002-3', authorUserId: 'teacher-001', parentCommentId: undefined,
      content: 'Fantastic observation about the water cycle! This is exactly the kind of scientific thinking I was hoping to see. A+ work!',
      visibility: 'public', isInstructorFeedback: true, edited: false, editDeadline: now,
      author: { id: 'teacher-001', displayName: 'Mr. Thompson' },
      createdAt: now, updatedAt: now,
    },
    {
      id: 'trc-005', entryId: 'tre-002-3', authorUserId: 'mock-student-004', parentCommentId: undefined,
      content: 'This is so cool! How long did it take for the condensation to start?',
      visibility: 'public', isInstructorFeedback: false, edited: false, editDeadline: now,
      author: { id: 'mock-student-004', displayName: 'Olivia Brown' },
      createdAt: now, updatedAt: now,
    },
    {
      id: 'trc-006', entryId: 'tre-002-3', authorUserId: 'student-001', parentCommentId: 'trc-005',
      content: 'It started on day 2! I placed it near the window where it gets morning sun.',
      visibility: 'public', isInstructorFeedback: false, edited: false, editDeadline: fifteenMinFromNow,
      author: { id: 'student-001', displayName: 'Emma Johnson' },
      createdAt: now, updatedAt: now,
    },
    // Michael's entry 2 - 1 comment
    {
      id: 'trc-007', entryId: 'tre-003-2', authorUserId: 'teacher-001', parentCommentId: undefined,
      content: 'Good choice with the mason jar, Michael. The drainage layer looks solid.',
      visibility: 'public', isInstructorFeedback: true, edited: false, editDeadline: twoDaysAgo,
      author: { id: 'teacher-001', displayName: 'Mr. Thompson' },
      createdAt: twoDaysAgo, updatedAt: twoDaysAgo,
    },
    // Michael's entry 3 - 2 comments
    {
      id: 'trc-008', entryId: 'tre-003-3', authorUserId: 'mock-student-006', parentCommentId: undefined,
      content: 'Succulents are the best! Mine is doing great too.',
      visibility: 'public', isInstructorFeedback: false, edited: false, editDeadline: twoDaysAgo,
      author: { id: 'mock-student-006', displayName: 'Liam Davis' },
      createdAt: twoDaysAgo, updatedAt: twoDaysAgo,
    },
    {
      id: 'trc-009', entryId: 'tre-003-3', authorUserId: 'teacher-001', parentCommentId: undefined,
      content: 'Great reflection on maintenance! Remember, succulents still need some indirect light. Looking forward to your final submission.',
      visibility: 'public', isInstructorFeedback: true, edited: false, editDeadline: twoDaysAgo,
      author: { id: 'teacher-001', displayName: 'Mr. Thompson' },
      createdAt: twoDaysAgo, updatedAt: twoDaysAgo,
    },
  ]

  for (const c of comments) {
    mockDb.insertWithId('trackRecordComments', c as TrackRecordComment)
  }
}

function seedNotifications(): void {
  const now = timestamp()
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
  const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()

  const notifications: Array<{
    id: string; userId: string; category: string; priority: string
    title: string; message: string; actionUrl?: string; actionLabel?: string
    relatedType?: string; relatedId?: string; isRead: boolean; readAt?: string
    sentViaEmail: boolean; sentViaPush: boolean; createdAt: string; updatedAt: string
  }> = [
    // General user (demo-user-001)
    { id: 'notif-001', userId: 'demo-user-001', category: 'gamification', priority: 'medium', title: 'Level Up!', message: 'Congratulations! You reached Level 5. Keep exploring!', actionUrl: '/gamification', actionLabel: 'View Progress', isRead: false, sentViaEmail: false, sentViaPush: false, createdAt: now, updatedAt: now },
    { id: 'notif-002', userId: 'demo-user-001', category: 'verification', priority: 'high', title: 'Track Record Approved', message: 'Your "Build a Birdhouse" track record has been verified and approved.', actionUrl: '/track-records/tr-001', actionLabel: 'View Track Record', relatedType: 'track_record', relatedId: 'tr-001', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },
    { id: 'notif-003', userId: 'demo-user-001', category: 'social_activity', priority: 'low', title: 'New Comment', message: 'Sarah Johnson commented on your track record.', actionUrl: '/track-records/tr-001', relatedType: 'track_record', relatedId: 'tr-001', isRead: true, readAt: yesterday, sentViaEmail: false, sentViaPush: false, createdAt: twoDaysAgo, updatedAt: yesterday },
    { id: 'notif-004', userId: 'demo-user-001', category: 'community_activity', priority: 'low', title: 'New Community Post', message: 'New discussion in "Outdoor Adventurers" community.', actionUrl: '/communities/community-002', relatedType: 'community', relatedId: 'community-002', isRead: true, readAt: twoDaysAgo, sentViaEmail: false, sentViaPush: false, createdAt: threeDaysAgo, updatedAt: twoDaysAgo },
    { id: 'notif-005', userId: 'demo-user-001', category: 'gifts', priority: 'medium', title: 'Gift Received!', message: 'Alex Parent sent you a challenge gift: "Pottery Basics"', actionUrl: '/gifts', actionLabel: 'View Gift', relatedType: 'gift', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },

    // Tier 2 student (student-001)
    { id: 'notif-010', userId: 'student-001', category: 'challenge_updates', priority: 'medium', title: 'New Assignment', message: 'Mr. Thompson assigned "Spring Nature Observation" to your class.', actionUrl: '/my-challenges', actionLabel: 'View Assignment', relatedType: 'assignment', relatedId: 'ca-001', isRead: false, sentViaEmail: false, sentViaPush: false, createdAt: now, updatedAt: now },
    { id: 'notif-011', userId: 'student-001', category: 'gamification', priority: 'low', title: 'Badge Earned', message: 'You earned the "First Steps" badge! Keep going.', actionUrl: '/gamification/badges', actionLabel: 'View Badge', isRead: false, sentViaEmail: false, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },
    { id: 'notif-012', userId: 'student-001', category: 'reminders', priority: 'medium', title: 'Assignment Due Soon', message: '"Spring Nature Observation" is due in 3 days.', actionUrl: '/my-challenges', relatedType: 'assignment', relatedId: 'ca-001', isRead: true, readAt: yesterday, sentViaEmail: true, sentViaPush: false, createdAt: twoDaysAgo, updatedAt: yesterday },

    // Tier 1 student (student-tier1-001)
    { id: 'notif-020', userId: 'student-tier1-001', category: 'challenge_updates', priority: 'medium', title: 'New Assignment', message: 'Mr. Thompson assigned "Backyard Bird Census" to your class.', actionUrl: '/my-challenges', relatedType: 'assignment', relatedId: 'ca-002', isRead: false, sentViaEmail: false, sentViaPush: false, createdAt: now, updatedAt: now },
    { id: 'notif-021', userId: 'student-tier1-001', category: 'teacher_feedback', priority: 'high', title: 'Teacher Feedback', message: 'Mr. Thompson left feedback on your nature observation.', isRead: false, sentViaEmail: false, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },

    // Parent (parent-001)
    { id: 'notif-030', userId: 'parent-001', category: 'parent_alerts', priority: 'high', title: 'Consent Request', message: 'Alex Student wants to join "Science Explorers" community. Approve or deny.', actionUrl: '/parent/consent', actionLabel: 'Review Request', isRead: false, sentViaEmail: true, sentViaPush: true, createdAt: now, updatedAt: now },
    { id: 'notif-031', userId: 'parent-001', category: 'parent_alerts', priority: 'medium', title: 'Weekly Activity Summary', message: 'Alex Student completed 2 challenges and earned 350 XP this week.', actionUrl: '/parent', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },
    { id: 'notif-032', userId: 'parent-001', category: 'gifts', priority: 'medium', title: 'Gift Approval Needed', message: 'Someone sent a challenge gift to Alex Student. Review and approve.', actionUrl: '/parent/gifts', actionLabel: 'Review Gift', relatedType: 'gift', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },

    // Teacher (teacher-001)
    { id: 'notif-040', userId: 'teacher-001', category: 'verification', priority: 'high', title: 'Track Record Submitted', message: 'Emma Johnson submitted a track record for "Spring Nature Observation".', actionUrl: '/school/reviews', actionLabel: 'Review Now', relatedType: 'track_record', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: now, updatedAt: now },
    { id: 'notif-041', userId: 'teacher-001', category: 'challenge_updates', priority: 'medium', title: 'Assignment Progress', message: '3 of 5 students have completed "Spring Nature Observation".', actionUrl: '/school/assignments', relatedType: 'assignment', relatedId: 'ca-001', isRead: false, sentViaEmail: false, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },
    { id: 'notif-042', userId: 'teacher-001', category: 'system', priority: 'low', title: 'New Challenge Available', message: 'A new "STEM Engineering" challenge is available to assign to your classes.', actionUrl: '/explore', isRead: true, readAt: twoDaysAgo, sentViaEmail: false, sentViaPush: false, createdAt: threeDaysAgo, updatedAt: twoDaysAgo },

    // School Admin (school-admin-001)
    { id: 'notif-050', userId: 'school-admin-001', category: 'school_admin', priority: 'high', title: 'Purchase Request', message: 'Mr. Thompson requested approval for "Advanced Robotics Kit" ($24.99).', actionUrl: '/school/purchase-requests', actionLabel: 'Review Request', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: now, updatedAt: now },
    { id: 'notif-051', userId: 'school-admin-001', category: 'school_admin', priority: 'medium', title: 'New Teacher Registration', message: 'Ms. Davis has registered and is pending approval for your school.', actionUrl: '/school/teachers', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },
    { id: 'notif-052', userId: 'school-admin-001', category: 'system', priority: 'low', title: 'Monthly School Report', message: 'Your school\'s February activity report is ready to view.', actionUrl: '/school', isRead: true, readAt: weekAgo, sentViaEmail: true, sentViaPush: false, createdAt: weekAgo, updatedAt: weekAgo },

    // Vendor (vendor-001)
    { id: 'notif-060', userId: 'vendor-001', category: 'vendor', priority: 'high', title: 'Challenge Approved', message: 'Your "DIY Craft Box" challenge has been approved and is now live!', actionUrl: '/vendor/challenges', actionLabel: 'View Challenge', relatedType: 'challenge', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: now, updatedAt: now },
    { id: 'notif-061', userId: 'vendor-001', category: 'vendor', priority: 'medium', title: 'New Order', message: 'You received a new order for "Build a Birdhouse" from Lincoln Elementary.', actionUrl: '/vendor/orders', actionLabel: 'View Order', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },
    { id: 'notif-062', userId: 'vendor-001', category: 'vendor', priority: 'low', title: 'Weekly Performance', message: 'Your challenges had 47 views and 12 completions this week.', actionUrl: '/vendor/analytics', isRead: true, readAt: twoDaysAgo, sentViaEmail: false, sentViaPush: false, createdAt: threeDaysAgo, updatedAt: twoDaysAgo },

    // Platform Admin (platform-admin-001)
    { id: 'notif-070', userId: 'platform-admin-001', category: 'system', priority: 'critical', title: 'Flagged Content', message: '3 new content items have been flagged for review.', actionUrl: '/admin/flagged', actionLabel: 'Review Now', isRead: false, sentViaEmail: true, sentViaPush: true, createdAt: now, updatedAt: now },
    { id: 'notif-071', userId: 'platform-admin-001', category: 'system', priority: 'high', title: 'Vendor Application', message: 'New vendor "Nature Explorer Kits" has applied for approval.', actionUrl: '/admin/vendors', actionLabel: 'Review Application', isRead: false, sentViaEmail: true, sentViaPush: false, createdAt: yesterday, updatedAt: yesterday },
    { id: 'notif-072', userId: 'platform-admin-001', category: 'system', priority: 'medium', title: 'Challenge Pending Review', message: '2 new challenges are waiting for content review.', actionUrl: '/admin/challenges/queue', isRead: false, sentViaEmail: false, sentViaPush: false, createdAt: twoDaysAgo, updatedAt: twoDaysAgo },
  ]

  for (const n of notifications) {
    mockDb.insertWithId('notifications', n as any)
  }
}

export function seedDatabase(): void {
  if (mockDb.isSeeded()) {
    console.log('Database already seeded')
    // Migration: Seed assignments if they don't exist yet
    const existingAssignments = mockDb.getAll('communityAssignments')
    if (existingAssignments.length === 0) {
      console.log('Migrating: Adding community assignments...')
      seedCommunityAssignments()
    }
    // Migration: Add demo user to Class 5A if not already a member
    const demoInClass5A = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === 'school-community-003' && m.userId === 'demo-user-001'
    )
    if (!demoInClass5A) {
      console.log('Migrating: Adding demo user to Class 5A...')
      const now = new Date().toISOString()
      mockDb.insertWithId('communityMemberships', {
        id: 'cm-022',
        communityId: 'school-community-003',
        userId: 'demo-user-001',
        role: 'member',
        status: 'active',
        canPost: true,
        canModerate: false,
        canInvite: false,
        joinedAt: '2025-08-01T00:00:00.000Z',
        createdAt: '2025-08-01T00:00:00.000Z',
        updatedAt: now,
      } as CommunityMembership)
    }
    // Migration: Add mock students if they don't exist
    const mockStudent = mockDb.findById('users', 'mock-student-002')
    if (!mockStudent) {
      console.log('Migrating: Adding mock students...')
      const now = new Date().toISOString()
      const mockStudents = [
        { id: 'mock-student-002', email: 'emma@school.edu', passwordHash: '', displayName: 'Emma Johnson', dateOfBirth: '2015-03-12', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-003', email: 'michael@school.edu', passwordHash: '', displayName: 'Michael Smith', dateOfBirth: '2015-05-22', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-004', email: 'sophia@school.edu', passwordHash: '', displayName: 'Sophia Garcia', dateOfBirth: '2015-01-08', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-005', email: 'james@school.edu', passwordHash: '', displayName: 'James Wilson', dateOfBirth: '2015-07-30', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-006', email: 'olivia@school.edu', passwordHash: '', displayName: 'Olivia Brown', dateOfBirth: '2015-11-15', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
      ]
      for (const student of mockStudents) {
        mockDb.insertWithId('users', student as unknown as User)
      }
    }
    // Migration: Update existing UserChallenges to link to community assignments
    // This fixes cases where the user started a challenge before the assignment tracking was added
    const allUserChallenges = mockDb.getAll<UserChallenge>('userChallenges')
    const allAssignments = mockDb.getAll<CommunityAssignment>('communityAssignments')
    const allMemberships = mockDb.getAll<CommunityMembership>('communityMemberships')

    for (const uc of allUserChallenges) {
      // Skip if already has a source set
      if (uc.source && uc.source !== 'self') continue

      // Find communities the user is a member of
      const userCommunityIds = allMemberships
        .filter(m => m.userId === uc.userId && m.status === 'active')
        .map(m => m.communityId)

      // Find an active assignment for this challenge in user's communities
      const assignment = allAssignments.find(
        a => a.challengeId === uc.challengeId &&
             a.status === 'active' &&
             userCommunityIds.includes(a.communityId)
      )

      if (assignment) {
        const community = mockDb.findById<Community>('communities', assignment.communityId)
        console.log(`Migrating: Linking UserChallenge ${uc.id} to assignment from ${community?.name}`)
        mockDb.update<UserChallenge>('userChallenges', uc.id, {
          source: 'class_assignment',
          communityAssignmentId: assignment.id,
          communityId: assignment.communityId,
          communityName: community?.name,
        } as Partial<UserChallenge>)
      }
    }
    // Migration: Seed rejection reason templates if they don't exist
    const existingTemplates = mockDb.getAll('rejectionReasonTemplates')
    if (existingTemplates.length === 0) {
      console.log('Migrating: Adding rejection reason templates...')
      seedRejectionReasonTemplates()
    }
    // Migration: Seed school data if it doesn't exist
    const existingSchools = mockDb.getAll('schools')
    if (existingSchools.length === 0) {
      console.log('Migrating: Adding school administration data...')
      seedSchoolData()
    }
    // Migration: Add demo user to teacherRoster if not already there
    const demoInTeacherRoster = mockDb.findOne<TeacherRoster>(
      'teacherRoster',
      (t) => t.userId === 'demo-user-001'
    )
    if (!demoInTeacherRoster) {
      console.log('Migrating: Adding demo user to teacher roster...')
      const now = new Date().toISOString()
      mockDb.insertWithId('teacherRoster', {
        id: 'tr-005',
        schoolId: 'school-001',
        userId: 'demo-user-001',
        role: 'admin',
        department: 'Administration',
        status: 'active',
        createdAt: now,
        updatedAt: now,
      } as TeacherRoster)
    }
    // Migration: Add additional teachers if they don't exist
    const teacher2 = mockDb.findById('users', 'teacher-002')
    if (!teacher2) {
      console.log('Migrating: Adding additional teachers...')
      const now = new Date().toISOString()
      const additionalTeachers = [
        { id: 'teacher-002', email: 'mrs.chen@lincoln-elementary.edu', passwordHash: '', displayName: 'Mrs. Chen', dateOfBirth: '1985-04-15', role: 'teacher', accountTier: 'standard', accountStatus: 'active', isUnder13: false, parentalConsentStatus: 'not_required', interests: ['art-creativity', 'cooking-food'], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'teacher-003', email: 'mr.rodriguez@lincoln-elementary.edu', passwordHash: '', displayName: 'Mr. Rodriguez', dateOfBirth: '1979-09-22', role: 'teacher', accountTier: 'standard', accountStatus: 'active', isUnder13: false, parentalConsentStatus: 'not_required', interests: ['science-exploration', 'technology-digital'], schoolId: 'school-001', createdAt: now, updatedAt: now },
      ]
      for (const t of additionalTeachers) {
        mockDb.insertWithId('users', t as unknown as User)
      }
    }
    // Migration: Add additional mock students (007-013) if they don't exist
    const mockStudent7 = mockDb.findById('users', 'mock-student-007')
    if (!mockStudent7) {
      console.log('Migrating: Adding additional mock students (007-013)...')
      const now = new Date().toISOString()
      const additionalStudents = [
        { id: 'mock-student-007', email: 'liam@school.edu', passwordHash: '', displayName: 'Liam Martinez', dateOfBirth: '2016-02-10', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-008', email: 'ava@school.edu', passwordHash: '', displayName: 'Ava Thompson', dateOfBirth: '2016-06-25', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-009', email: 'noah@school.edu', passwordHash: '', displayName: 'Noah Davis', dateOfBirth: '2016-01-03', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-010', email: 'isabella@school.edu', passwordHash: '', displayName: 'Isabella Anderson', dateOfBirth: '2014-09-18', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-011', email: 'ethan@school.edu', passwordHash: '', displayName: 'Ethan Taylor', dateOfBirth: '2014-11-30', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-012', email: 'mia@school.edu', passwordHash: '', displayName: 'Mia Thomas', dateOfBirth: '2014-04-07', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
        { id: 'mock-student-013', email: 'lucas@school.edu', passwordHash: '', displayName: 'Lucas Jackson', dateOfBirth: '2014-08-14', role: 'student', accountTier: 'tier_2_full', accountStatus: 'active', isUnder13: true, parentalConsentStatus: 'granted', interests: [], schoolId: 'school-001', createdAt: now, updatedAt: now },
      ]
      for (const s of additionalStudents) {
        mockDb.insertWithId('users', s as unknown as User)
      }
    }
    // Migration: Seed community goals if they don't exist
    const existingGoals = mockDb.getAll('communityGoals')
    if (existingGoals.length === 0) {
      console.log('Migrating: Adding community goals...')
      seedCommunityGoals()
    }
    // Migration: Seed student track records if they don't exist
    const existingSchoolTR = mockDb.findById('trackRecords', 'tr-school-002')
    if (!existingSchoolTR) {
      console.log('Migrating: Adding student track records with entries...')
      seedStudentTrackRecords()
    }
    // Migration: Seed emoji reactions if they don't exist
    const existingReactions = mockDb.getAll('entryReactions')
    if (existingReactions.length === 0 && existingSchoolTR) {
      console.log('Migrating: Adding emoji reactions to track record entries...')
      seedStudentTrackRecords() // re-seeds reactions along with entries (idempotent for entries)
    }
    return
  }

  console.log('Seeding database...')

  seedCategories()
  seedUsers()
  seedVendors()
  seedProviderFeedback()
  seedSchoolData()
  seedChallenges()
  seedUserChallenges()
  seedCommunities()
  seedGamification()
  seedGifts()
  seedCommunityAssignments()
  seedStudentTrackRecords()
  seedRejectionReasonTemplates()
  seedLearningPaths()
  seedPortfolios()
  seedEvents()
  seedReflectionPrompts()
  seedReports()
  seedCommunityGoals()
  seedNotifications()

  mockDb.markSeeded()
  console.log('Database seeded successfully')
}

/**
 * Reset and reseed database
 */
export function resetDatabase(): void {
  console.log('Resetting database...')
  mockDb.clearAll()
  localStorage.removeItem('docurious-seeded')
  seedDatabase()
}
